import { useCallback, useEffect, useMemo, useState } from "react";
// MS64b crash fix: EmptyState uses <Link to="/team"> but only
// useNavigate was imported. First-time signed-in users with zero
// celebrations would hit a runtime error. Adding Link.
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { startOfDay, addDays, format, parseISO, differenceInCalendarDays, isSameDay } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
// MS45: chrome (sidebar, logo wordmark, nav, sign-out, AutoPilot
// card) lives in ConciergeLayout. Dashboard renders only its page
// content (list pane + detail pane) inside the layout's <Outlet />.
// The badge-count queries (team count, unread count) also moved to
// the layout — react-query dedupes the underlying cache so there's
// no double-fetch even when both layout + page consume the same
// hook elsewhere.
import {
  useProfile,
  useAllCelebrations,
  useTeamMembers,
  useUpcomingCelebrations,
  useSkippedCelebrations,
  useApprovedCelebrationsPendingOrder,
  useOrdersList,
  useRecentGiftRequests,
  useWishlist,
  useCompanySettings,
  useHasPaymentMethod,
  useCompanyAddresses,
  useManualApprovalProgress,
  MANUAL_APPROVALS_FOR_AUTOPILOT,
  type CelebrationRow,
  type OrderListRow,
  type WishlistRow,
} from "@/hooks/queries";
import {
  parseGiftRequest,
  categoryLabel,
  categoryEmoji,
  occasionLabel,
} from "@/lib/giftRequest";
import { ApprovalModal, type ApprovalRecommendation } from "@/components/concierge/ApprovalModal";
import { CalendarView } from "@/components/concierge/CalendarView";
// MS572 + MS575: drawer shell for in-dashboard concierge chat.
// Mounted at the page root but only opens when `drawerRecipient` is
// non-null. MS575 wired ApprovalModal's "Adjust" button to
// setDrawerRecipient(...) so the buyer stays on the dashboard while
// adjusting a recipient's gift — replacing the pre-MS575 navigate to
// /chat that lost dashboard context entirely.
import { useRegisterFloatingConciergeSuppression, useFloatingConciergeUi } from "@/contexts/FloatingConciergeContext";
import { useOpenAuthedConcierge } from "@/hooks/useOpenAuthedConcierge";
import {
  DashboardConciergeDrawer,
  type DashboardConciergeDrawerRecipient,
} from "@/components/concierge/DashboardConciergeDrawer";
// MS582: 1920px+ viewport detection so the drawer can flip from
// overlay-Sheet to inline push-column mode and the grid template
// can swap to a 3-column layout (list + push-drawer) in lockstep.
// Returns false on jsdom / SSR so unit tests see overlay-mode
// behaviour unless they explicitly mock matchMedia.
import { useIsLargeScreen } from "@/hooks/use-large-screen";
// MS556: pool-status widget. Reads the active company_budget_periods
// row + spend/scheduled/adjustment totals. Renders above the AutoPilot
// warning when the company has a pooled cadence configured; hides for
// per_celebration / unconfigured companies so the existing dashboard
// layout is unchanged for non-budget users.
import { DashboardPoolWidget } from "@/components/dashboard/DashboardPoolWidget";
// MS559: first-time "set up a budget" nudge. Renders in the same slot
// as the pool widget but only when pool_cadence is null (never
// configured) — the two are mutually exclusive. Dismissible + persists
// the dismissal in localStorage so it never re-nags. Keeps the budget
// feature discoverable from the Dashboard without nagging the 90% of
// companies that never set a pool.
import { DashboardBudgetNudge } from "@/components/dashboard/DashboardBudgetNudge";
// MS378: SmartIntentBar + InlineConcierge imports removed. The inline
// classifier-driven chat surface on /dashboard was replaced by the
// FloatingConcierge widget which now appears on /dashboard like every
// other authed tab — single chat surface across the app. The dashboard
// keeps its "Up next" list + filter pills + calendar view as the
// primary content; chat is one tap away in the bottom-right corner.
//
// The MS63a IDLE ↔ IN_CHAT state machine that lived here is also
// gone — the floating widget handles open/close + session lifecycle
// internally. chat-concierge still uses list_team_members +
// list_upcoming_celebrations to resolve recipient names against the
// company directory; that tool surface is unchanged.
// MS263: prefetch heavier tab queries so tab navigation feels instant.
import { usePrefetchDashboardTabs } from "@/hooks/usePrefetchDashboardTabs";
// MS64a + MS606: saved-gift tiles use unified checkout with add-ons.
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import { wishlistRowToSkuCardData } from "@/lib/wishlistRowToSkuCardData";
import {
  applyCelebrationCheckoutResult,
  openApprovedCelebrationCheckout,
  openRecommendedCelebrationCheckout,
} from "@/lib/openApprovedCelebrationCheckout";
import { ordersFulfillmentReturn } from "@/lib/fulfillmentReturn";
import {
  approvedCelebrationToRow,
  isPaymentFailedOrder,
  normalizeDashboardFilter,
  type DashboardListFilter,
} from "@/lib/dashboardHome";
import { canRevitalizeCelebration } from "@/lib/celebrationDates";
import {
  getReturnedToScheduleIds,
  pinReturnedToSchedule,
} from "@/lib/returnedToScheduleStorage";
import { pickDefaultCompanyAddress } from "@/lib/pickDefaultCompanyAddress";
import { cancelOpenOrdersForCelebration, assertCelebrationCanBeSkipped } from "@/lib/cancelCelebrationOrders";
import { DashboardAttentionStrip } from "@/components/dashboard/DashboardAttentionStrip";
import {
  DASHBOARD_LIST_RANGE_OPTIONS,
  dashboardListRangeBounds,
  normalizeDashboardListRange,
  type DashboardListRangeKey,
} from "@/lib/dashboardListRange";
// MS575: celebrationToChatUrl import removed — Dashboard no longer
// opens the in-dashboard concierge drawer for pick-gift; Adjust uses
// drawer instead. The helper is still used by Calendar / Notifications /
// Orders for their own celebration→chat handoffs; this file just
// doesn't reach for it anymore.
import {
  generateRecommendation,
  generateRecommendationFromCatalogue,
} from "@shared/recommendation";
import { teamMemberProfileHints } from "@/lib/teamMemberProfileHints";
import { tierLabel } from "@/lib/tierLabels";
import { formatSkuName } from "@/lib/formatSkuName"; // MS407: consistent voucher/prefix stripping on dashboard wishlist chips
// MS601 (Phase 6 observability): the dashboard's real failure modes
// (upcoming-list load, gift-approval persist, skip, v2 recommendation
// generation) currently fail SILENTLY — an ErrorCard / toast / swallowed
// fallback, but nothing reaches Sentry, so a regression of the bugs
// phases 1–5 fixed would page nobody. Extend the MS526 capture idiom
// (Orders.tsx / OrderConfirmSheet.tsx) to those sites + fire the one
// business event the dashboard was missing. PII stays out: we capture
// the Postgres/RLS error object + a `scope` tag ONLY (sentry.ts already
// runs sendDefaultPii:false / maskAllText), and the analytics payloads
// carry ids/counts, never recipient names / emails / gift details.
import { Sentry } from "@/lib/sentry";
import { track, EVENTS } from "@/lib/analytics";
import { BulkCelebrationModal } from "@/components/bulk/BulkCelebrationModal";
import { useBulkProgram, useBulkRun } from "@/hooks/useBulkCelebrations";
import { isBulkProgramActive } from "@/lib/bulkCelebrations";
import type { BulkCheckoutSeed } from "@/lib/openBulkCelebrationCheckout";

// New concierge dashboard. Replaces the previous SaaS-typical six-block
// dashboard with the calm A-style list + warm B-style approval modal
// from the concierge-v1.html prototype. See:
//   - lembra-design-experimentation/concierge-v1.html (visual reference)
//   - lembra-design-experimentation/dashboard-inventory.md (cut/move map)
//
// This page bypasses AppLayout, it renders its own sidebar so the
// prototype's calm hierarchy holds. The sidebar nav matches the new
// information architecture spec'd in the inventory doc.
//
// MS97 + MS525: Recommendation data now flows through v2 for the
// selected celebration. recommendationFor() still computes the
// lightweight (date-derived) fields synchronously — status pills,
// time labels, day groupings, awaiting/autopilot counts — but the
// rich fields (productSummary, bakery, imageUrl, skuId) come from
// generateRecommendationFromCatalogue via useQuery. Per-recipient
// hash variation lives inside the v2 picker (pickSkuForCategory),
// so different teammates in the same company get different cakes
// instead of every tile reading "A dozen assorted cupcakes from
// Cupcake Central." The non-selected celebrations in the list pane
// don't run the v2 query (they only read date-derived fields).

// Sprint 2.5a B.2 P2-5, rotating greeting helper. Day-of-year mod 5
// keeps the greeting stable across an entire day (no jitter inside a
// session) but cycles across the week so a daily customer doesn't see
// the same line every login. The set is intentionally restrained —
// all variants stay quiet + brand-aligned (no exclamation marks, no
// emoji), no novelty greetings that would feel performative.
const GREETING_VARIANTS = [
  (n: string) => `Hi ${n}.`,
  (n: string) => `Welcome back, ${n}.`,
  (n: string) => `Good to see you, ${n}.`,
  (n: string) => `Hi again, ${n}.`,
  (n: string) => `Hey ${n}.`,
];

function greetingFor(firstName: string): string {
  const today = new Date();
  // Day-of-year via UTC to avoid timezone-driven flicker around midnight.
  const start = Date.UTC(today.getUTCFullYear(), 0, 0);
  const diff = today.getTime() - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  const variant = GREETING_VARIANTS[dayOfYear % GREETING_VARIANTS.length];
  return variant(firstName);
}

// MS45: NAV_CONCIERGE / NAV_SETTINGS now live in ConciergeLayout.

// MS378: dashboardChatSuggestions removed — it only fed the inline
// SmartIntentBar's quick-chip row, which no longer renders. The
// FloatingConcierge widget shows its own evergreen quick-replies
// ("What's coming up?", "Skip a celebration", "Update someone's
// preferences", "Send a gift") that work across every authed tab.


export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // MS140: memoise so `today` is stable across renders. Previously
  // `startOfDay(new Date())` created a fresh Date each render →
  // every downstream useMemo that listed `today` in deps re-ran on
  // every render (groups, filteredGroups, the celebrations derivation
  // below, etc.). With empty deps the value is fixed for the
  // component's lifetime; the worst case if the dashboard stays
  // mounted across midnight is "today's celebrations" still shows
  // yesterday's day-of-week, which is an acceptable trade for the
  // re-render savings on the busy dashboard.
  const today = useMemo(() => startOfDay(new Date()), []);
  const [listRange, setListRange] = usePersistedState<DashboardListRangeKey>(
    "lembra:dashboard-list-range",
    "8w",
    {
      validate: (v): v is DashboardListRangeKey =>
        typeof v === "string" && normalizeDashboardListRange(v) !== null,
    },
  );
  const listRangeBounds = useMemo(
    () => dashboardListRangeBounds(listRange, today),
    [listRange, today],
  );
  const listRangeMeta = useMemo(
    () => DASHBOARD_LIST_RANGE_OPTIONS.find((o) => o.key === listRange)!,
    [listRange],
  );

  // Demo mode: when there's no signed-in user, the page is being viewed
  // as a public mock (e.g. /dashboard-demo). Fall back to seeded data so
  // the layout has something to render. Real users with sessions see
  // their real celebrations.
  const isDemo = !user;
  // MS84: queryClient needed so handleSkip can invalidate the
  // celebrations cache after a status flip — without this, the
  // skipped row sticks around in the dashboard list until React-
  // Query's natural stale-time triggers a refetch.
  const queryClient = useQueryClient();

  const profileQuery = useProfile();
  const companyId = profileQuery.data?.company_id ?? null;

  // MS263: warm the caches for /team, /orders, /wishlist,
  // /notifications in the background as soon as we know who the
  // user is. Subsequent tab navigation resolves from cache instead
  // of a 3-4s network round-trip (Lucky's report on 2026-05-18).
  // Fire-and-forget; failures are silent and the per-page useQuery
  // retries on its own.
  usePrefetchDashboardTabs(companyId, user?.id);
  const fullName = profileQuery.data?.full_name ?? "";
  const firstName = isDemo ? "Lucky" : fullName.split(" ")[0] || "there";
  // Sprint 2.5a B.2 P2-5, rotate the greeting line so the same
  // string isn't sitting at the top of /dashboard every visit. Day-
  // of-year modulo keeps it stable for the whole day (the customer
  // sees one consistent greeting per session) but cycles through
  // the set across the week. No randomness inside a session, that
  // would feel jittery if the page re-rendered.
  const greetingLine = greetingFor(firstName);

  // MS45: team-count + unread-count queries moved to ConciergeLayout
  // (it owns the sidebar that displays those badges). Dashboard only
  // queries the data IT renders — the upcoming celebrations list,
  // the recent gift requests on the empty-detail tile, and the
  // wishlist for the saved-gifts tile.
  const upcomingQuery = useUpcomingCelebrations(companyId, {
    from: listRangeBounds.from,
    to: listRangeBounds.to,
    limit: listRangeBounds.limit,
  });
  const allCelebrationsQuery = useAllCelebrations(isDemo ? null : companyId);
  const teamMembersQuery = useTeamMembers(isDemo ? null : companyId);
  const [filterPersisted, setFilterPersisted] = usePersistedState<
    DashboardListFilter | "locked_in"
  >("lembra:dashboard-filter", "all", {
    validate: (v): v is DashboardListFilter | "locked_in" =>
      typeof v === "string" && normalizeDashboardFilter(v) !== null,
  });
  const filter = useMemo(
    () => normalizeDashboardFilter(filterPersisted) ?? "all",
    [filterPersisted],
  );
  const setFilter = useCallback(
    (next: DashboardListFilter) => setFilterPersisted(next),
    [setFilterPersisted],
  );
  const skippedQuery = useSkippedCelebrations(isDemo ? null : companyId, {
    from: listRangeBounds.from,
    to: listRangeBounds.to,
    limit: listRangeBounds.limit,
  });
  const needsCheckoutQuery = useApprovedCelebrationsPendingOrder(
    isDemo ? null : companyId,
  );
  const ordersListQuery = useOrdersList(isDemo ? null : companyId);
  // MS79: company settings feed recommendationFor's tier + teamSize
  // resolution. companyDefaultTier and companyTeamSize default to
  // null when the wizard hasn't run yet; recommendationFor falls
  // back to its prior hardcoded values in that case so nothing
  // regresses. Demo skips this — buildDemoCelebrations doesn't
  // belong to a real company.
  const companySettingsQuery = useCompanySettings(isDemo ? null : companyId);
  const companyDefaultTier = companySettingsQuery.data?.default_tier ?? null;
  const companyTeamSize = companySettingsQuery.data?.team_size ?? null;
  // MS389: AutoPilot+no-card warning. autopilot is the *new* strategic
  // flag (concierge_autopilot_enabled) — `autopay_enabled` is the
  // retired single-bakery autopay column. We only show the banner once
  // both queries have actually resolved (data !== undefined) so a
  // first-paint hasn't-loaded-yet state can't briefly render the
  // warning for a company that does have a card. Demo skips entirely.
  const autopilotOn = companySettingsQuery.data?.concierge_autopilot_enabled === true;
  const hasPaymentMethodQuery = useHasPaymentMethod(isDemo ? null : companyId);
  const hasPaymentMethod = hasPaymentMethodQuery.data === true;
  const companyAddressesQuery = useCompanyAddresses(isDemo ? null : companyId);
  const manualApprovalQuery = useManualApprovalProgress(isDemo ? null : companyId);
  const manualApprovalUnlocked = manualApprovalQuery.data?.unlocked ?? false;
  const manualApprovalCount = manualApprovalQuery.data?.count ?? 0;
  const manualApprovalRemaining =
    manualApprovalQuery.data?.remaining ?? MANUAL_APPROVALS_FOR_AUTOPILOT;
  const showAutopilotTrustGateBanner =
    !isDemo &&
    !autopilotOn &&
    !manualApprovalUnlocked &&
    manualApprovalCount > 0 &&
    manualApprovalCount < MANUAL_APPROVALS_FOR_AUTOPILOT;
  const showAutopilotNoCardWarning =
    !isDemo &&
    companySettingsQuery.data !== undefined &&
    hasPaymentMethodQuery.data !== undefined &&
    autopilotOn &&
    !hasPaymentMethod;
  // Sprint 1.7r: surface chat / browse-originated orders so a freshly
  // reconciled customer (just signed up via magic link after paying for
  // a gift) sees their order on the dashboard instead of an empty
  // "add team members" prompt. Demo mode skips this, public mock has
  // no real orders to read.
  const giftRequestsQuery = useRecentGiftRequests(isDemo ? null : companyId, 3);
  const recentGiftRequests = giftRequestsQuery.data ?? [];
  // Sprint 2.2e, saved-gift cluster on the empty-detail pane.
  const wishlistQuery = useWishlist(isDemo ? null : user?.id);
  const wishlist = wishlistQuery.data ?? [];

  // MS601: page Sentry when the upcoming-celebrations load fails. The
  // list pane renders an ErrorCard (below, ~upcomingQuery.isError) but
  // never reported the failure — a regression of the dashboard loader
  // would page nobody. Mirrors Orders.tsx's MS526 useEffect-on-isError
  // idiom: React Query holds the error reference stable across retries
  // so this fires ~once per failure session. Demo mode never errors
  // (no query runs), so no isDemo guard is needed. Captures the error
  // object + scope tag ONLY — no recipient/company PII.
  useEffect(() => {
    if (upcomingQuery.isError && upcomingQuery.error) {
      Sentry.captureException(upcomingQuery.error, {
        tags: { scope: "dashboard_upcoming" },
      });
    }
  }, [upcomingQuery.isError, upcomingQuery.error]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  /** Pinned celebration row for ApprovalModal — avoids stale selected when filters/view change while open. */
  const [modalCelebration, setModalCelebration] = useState<CelebrationRow | null>(null);
  const [returnedPinIds, setReturnedPinIds] = useState<string[]>(() =>
    getReturnedToScheduleIds(),
  );
  const [view, setView] = usePersistedState<"list" | "calendar">(
    "lembra:dashboard-view",
    "list",
    {
      validate: (v): v is "list" | "calendar" => v === "list" || v === "calendar",
    },
  );

  // MS140: memoise celebrations array. Calendar = full timeline; list = 8 weeks.
  const calendarCelebrations = useMemo((): CelebrationRow[] => {
    if (isDemo) return buildDemoCelebrations(today);
    const memberMap = new Map((teamMembersQuery.data ?? []).map((m) => [m.id, m]));
    return (allCelebrationsQuery.data ?? []).map((c) => {
      const m = memberMap.get(c.team_member_id);
      return {
        id: c.id,
        date: c.date,
        type: c.type,
        status: c.status,
        team_members: m
          ? {
              id: m.id,
              name: m.name,
              dietary_requirements: m.dietary_requirements,
              tier_override: (m as { tier_override?: string | null }).tier_override,
              start_date: m.start_date,
              hobbies: (m as { hobbies?: string[] | null }).hobbies,
              favourite_foods: (m as { favourite_foods?: string[] | null }).favourite_foods,
              gift_preferences: (m as { gift_preferences?: unknown }).gift_preferences,
              personal_notes: (m as { personal_notes?: string | null }).personal_notes,
            }
          : { name: "Team member" },
      };
    });
  }, [isDemo, today, allCelebrationsQuery.data, teamMembersQuery.data]);

  const celebrations = useMemo(() => {
    if (isDemo) return buildDemoCelebrations(today);
    if (view === "calendar") return calendarCelebrations;
    if (filter === "skipped") return skippedQuery.data ?? [];
    if (filter === "needs_checkout") {
      return (needsCheckoutQuery.data ?? []).map(approvedCelebrationToRow);
    }
    return upcomingQuery.data ?? [];
  }, [
    isDemo,
    today,
    view,
    calendarCelebrations,
    upcomingQuery.data,
    skippedQuery.data,
    needsCheckoutQuery.data,
    filter,
  ]);

  // MS378: chat state machine + handleSmartIntentResult callback
  // removed along with the SmartIntentBar / InlineConcierge surface.
  // The FloatingConcierge widget owns chat session state internally.
  // The browse / nav verdict-routing the bar did is now redundant —
  // a user can navigate via the sidebar or just ask the floating
  // chat directly ("show me Marcus's profile" → chat-concierge's
  // list_team_members + a hyperlink response).

  // MS64a + MS606 — unified checkout with add-ons for saved-gift tiles.
  const savedGiftsCheckout = useProductCheckoutFlow({ cameFrom: "dashboard" });
  const { confirmingSku, handleConfirmFromModal, handleOpenConfirmSheet } = savedGiftsCheckout;
  const [celebrationCheckout, setCelebrationCheckout] = useState<{
    celebrationId: string;
    recipientId: string;
    recipientName: string;
    occasion: string;
    defaultDate: string;
    defaultAddress?: string;
  } | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkCheckout, setBulkCheckout] = useState<BulkCheckoutSeed | null>(null);
  const bulkProgramQuery = useBulkProgram(companyId ?? undefined);
  const bulkRunQuery = useBulkRun(companyId ?? undefined, bulkProgramQuery.data ?? undefined);

  // MS572 + MS575: state for the in-dashboard concierge drawer.
  // Non-null means the drawer is open with that recipient loaded; null
  // closes it. MS575 wires this from ApprovalModal's "Adjust" handler
  // (handleAdjust below) so the buyer stays on the dashboard while
  // adjusting a recipient's gift — pre-MS575 that path navigated to
  // /chat and lost dashboard context. The drawer itself handles draft
  // restore + tab persistence via MS573 / MS574.
  const [drawerRecipient, setDrawerRecipient] =
    useState<DashboardConciergeDrawerRecipient | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const fromOrdersHandoff = searchParams.get("from") === "orders";
  const ordersHandoffReturn = useMemo(
    () =>
      fromOrdersHandoff
        ? ordersFulfillmentReturn(() => {
            setDrawerRecipient(null);
            setCelebrationCheckout(null);
            navigate("/orders?tab=active");
          })
        : null,
    [fromOrdersHandoff, navigate],
  );

  // MS594: deep-link from /orders "Needs order" → open concierge drawer.
  useEffect(() => {
    const drawerCelebrationId = searchParams.get("drawer");
    if (!drawerCelebrationId || drawerRecipient || isDemo) return;

    const openFromCelebration = async () => {
      let cel =
        celebrations.find((c) => c.id === drawerCelebrationId) ?? null;
      if (!cel && companyId) {
        const { data } = await supabase
          .from("celebrations")
          .select(
            "id, type, date, team_members(id, name, dietary_requirements)",
          )
          .eq("id", drawerCelebrationId)
          .eq("company_id", companyId)
          .maybeSingle();
        cel = (data as CelebrationRow | null) ?? null;
      }
      if (!cel) return;

      const tm = cel.team_members;
      const memberRaw = Array.isArray(tm) ? tm[0] : tm;
      if (!memberRaw?.id) return;

      setDrawerRecipient({
        id: memberRaw.id,
        name: memberRaw.name,
        occasion: cel.type,
        celebrationId: cel.id,
        celebrationDate: cel.date,
        dietaryRequirements: memberRaw.dietary_requirements ?? undefined,
      });

      const next = new URLSearchParams(searchParams);
      next.delete("drawer");
      setSearchParams(next, { replace: true });
    };

    void openFromCelebration();
  }, [
    celebrations,
    companyId,
    drawerRecipient,
    isDemo,
    searchParams,
    setSearchParams,
  ]);

  const runCelebrationCheckout = useCallback(
    async (celebrationId: string) => {
      if (!companyId) return;
      const result = await openApprovedCelebrationCheckout(celebrationId, companyId);
      applyCelebrationCheckoutResult(result, {
        onCheckout: (sku, ctx) => {
          const defaultAddress = pickDefaultCompanyAddress(companyAddressesQuery.data);
          setCelebrationCheckout({
            ...ctx,
            defaultAddress: defaultAddress ?? undefined,
          });
          handleConfirmFromModal(sku);
        },
        onPickGift: (ctx) => {
          setDrawerRecipient({
            id: ctx.recipientId,
            name: ctx.recipientName,
            occasion: ctx.occasion,
            celebrationId: ctx.celebrationId,
            celebrationDate: ctx.defaultDate,
            dietaryRequirements: ctx.dietaryRequirements,
          });
        },
        onError: (message) => toast.error(message),
      });
    },
    [companyAddressesQuery.data, companyId, handleConfirmFromModal],
  );

  // Deep-link from /orders (legacy completeOrder) or in-app handoff.
  useEffect(() => {
    const completeOrderId = searchParams.get("completeOrder");
    if (!completeOrderId || isDemo || confirmingSku || !companyId) return;

    const next = new URLSearchParams(searchParams);
    next.delete("completeOrder");
    next.delete("from");
    setSearchParams(next, { replace: true });
    void runCelebrationCheckout(completeOrderId);
  }, [
    companyId,
    confirmingSku,
    isDemo,
    runCelebrationCheckout,
    searchParams,
    setSearchParams,
  ]);

  // MS582: ≥1920px monitors get the push-drawer 3-column layout
  // (list + push-drawer, detail pane hides). <1920px keeps the
  // overlay-Sheet 2-column layout (list + 360px detail rail).
  // Why both decisions in ONE state: the grid template and the
  // drawer's render mode are coupled — if the drawer is in push
  // mode the grid must also have the column to host it. Hooking
  // here once and threading the boolean down keeps that contract
  // tight.
  const isLargeScreen = useIsLargeScreen();
  const drawerOpen = drawerRecipient !== null;
  const usePushDrawer = isLargeScreen && drawerOpen;

  const floatingCheckoutActive =
    Boolean(celebrationCheckout) ||
    Boolean(confirmingSku) ||
    Boolean(savedGiftsCheckout.viewingSku);
  useRegisterFloatingConciergeSuppression("dashboard-drawer", drawerOpen);
  useRegisterFloatingConciergeSuppression("dashboard-checkout", floatingCheckoutActive);

  // MS572 dev trigger (`lembra:dev-open-concierge-drawer` CustomEvent
  // listener) removed in MS575. The drawer now opens via the real
  // ApprovalModal "Adjust" handoff in handleAdjust below; the dev event
  // had no in-UI affordance and was strictly a manual-QA shim. Firing
  // the event from the console is now a no-op — there's no listener.

  // Auto-select the first "awaiting your nod" celebration so the right
  // pane is meaningful on first paint instead of empty.
  const selected = useMemo(() => {
    if (selectedId) {
      const inView = celebrations.find((c) => c.id === selectedId);
      if (inView) return inView;
      // Keep modal context when status flips (e.g. revitalize on Skipped filter).
      if (modalOpen) {
        return calendarCelebrations.find((c) => c.id === selectedId) ?? null;
      }
      return null;
    }
    return celebrations[0] ?? null;
  }, [selectedId, celebrations, calendarCelebrations, modalOpen]);

  // MS79: thread company defaults through so recommendationFor honours
  // companies.default_tier + companies.team_size (with team_member.
  // tier_override winning when set).
  const recOpts = useMemo(
    () => ({ companyDefaultTier, companyTeamSize, conciergeAutopilotOn: autopilotOn }),
    [companyDefaultTier, companyTeamSize, autopilotOn],
  );

  // MS525: the synchronous stub still drives the lightweight surfaces
  // (status pills, day groupings, time labels, awaiting/autopilot
  // counts) — those fields are date-derived, not recipient-specific,
  // so they don't need the v2 catalogue query.
  //
  // The right-pane DetailPane + ApprovalModal *do* consume the rich
  // productSummary / bakery / imageUrl / skuId fields, and THAT is
  // where every team member used to land on "A dozen assorted cupcakes
  // from Cupcake Central" — v1's bakery routing is keyed on
  // (tier × dietary axis) only, with zero per-recipient variation.
  //
  // Fix: fetch the rich fields via v2 (DB-backed, hash-shuffled per
  // recipient) for the SELECTED celebration only, and merge them over
  // the sync stub. Other celebrations in the list pane don't run the
  // query — they only need the lightweight fields.
  const syncStub = selected ? recommendationFor(selected, today, recOpts) : null;
  const v2Query = useQuery({
    queryKey: [
      "dashboard-recommendation-v2",
      selected?.id,
      syncStub?.tier,
      // Dietaries + occasion + date are the cache-invalidation
      // surface — if any change the picked SKU may change too.
      syncStub?.occasion,
      syncStub?.date,
      // Dietaries are deeply equal within a render; encode them so
      // useQuery's shallow compare doesn't fight us.
      JSON.stringify(
        (() => {
          const tm = selected?.team_members;
          const tmFlat = Array.isArray(tm) ? tm[0] : tm;
          return {
            dietaries: tmFlat?.dietary_requirements ?? [],
            profile: teamMemberProfileHints(tmFlat ?? undefined),
          };
        })(),
      ),
    ],
    enabled: Boolean(selected && syncStub),
    queryFn: async () => {
      if (!selected || !syncStub) return null;
      const tm = selected.team_members;
      const tmFlat = Array.isArray(tm) ? tm[0] : tm;
      const dietaries = tmFlat?.dietary_requirements ?? [];
      const profileHints = teamMemberProfileHints(tmFlat ?? undefined);
      // bucketForTeamSize is internal to recommendation.ts; the sync
      // stub already computed the right inputs for v1, so we mirror
      // them here. teamSize bucket is derived in recommendationFor
      // via TEAM_SIZE_HEADCOUNT; we re-derive identically below to
      // avoid exporting an extra helper out of the shared file.
      const TEAM_SIZE_HEADCOUNT: Record<string, number> = {
        "1-10": 8,
        "11-25": 18,
        "26-50": 30,
        "51-100": 75,
        "100+": 150,
        very_small: 6,
        small: 10,
        medium: 18,
        large: 30,
        very_large: 75,
        team: 8,
        floor: 18,
        office: 30,
        big_days: 75,
      };
      const teamSize =
        (companyTeamSize && TEAM_SIZE_HEADCOUNT[companyTeamSize]) || 18;
      return await generateRecommendationFromCatalogue(
        {
          tier: syncStub.tier,
          teamSize,
          dietaries,
          occasion: syncStub.occasion,
          recipientName: syncStub.recipientName,
          date: syncStub.date,
          deliveryWindow: "morning_early",
          ...profileHints,
        },
        supabase,
      );
    },
    // Same SKU pool, same hash → same pick. No reason to refetch
    // until the inputs (in the queryKey) change.
    staleTime: 5 * 60 * 1000,
  });
  // Merge: keep the sync stub's lightweight fields (statusKey,
  // timeLabel, editWindowCloses, etc) and override the rich fields
  // (productSummary, bakery, imageUrl, productLine, rationaleDetail,
  // totalCents, skuId, vendorCategory) when v2 has resolved. While
  // v2 is loading, the buyer sees v1's curated copy — the same
  // experience they had pre-MS525, just for ~50ms.
  const recommendation = useMemo(() => {
    if (!syncStub) return null;
    const v2 = v2Query.data;
    if (!v2) return syncStub;
    return {
      ...syncStub,
      bakery: v2.bakery,
      productSummary: v2.productSummary,
      productLine: v2.productLine,
      cardDetail: v2.cardDetail,
      rationaleDetail: v2.rationaleDetail,
      totalCents: v2.totalCents,
      imageUrl: v2.imageUrl,
      imageAlt: v2.imageAlt,
      vendorCategory: v2.vendorCategory,
      skuId: v2.skuId,
    };
  }, [syncStub, v2Query.data]);

  // MS601: capture the SWALLOWED v2 recommendation-generation error. When
  // generateRecommendationFromCatalogue fails (sparse catalogue, RLS,
  // schema drift) the recommendation memo above silently falls back to
  // v1's curated copy — good UX, but the regression was invisible. We
  // capture the error here WITHOUT changing the fallback: the buyer still
  // sees v1 copy; we just stop losing the signal. Error object + scope
  // tag only (the error is an Error/Postgres object, no recipient PII).
  useEffect(() => {
    if (v2Query.isError && v2Query.error) {
      Sentry.captureException(v2Query.error, {
        tags: { scope: "dashboard_reco_v2" },
      });
    }
  }, [v2Query.isError, v2Query.error]);

  // Group celebrations by day for the list pane.
  const groups = useMemo(() => groupByDay(celebrations, today), [celebrations, today]);

  // Filter chip wiring, kept on the client because we already loaded
  // 8 weeks of celebrations. Refactor to server-side filter once volume
  // makes that meaningful.
  const returnedCelebrations = useMemo(() => {
    if (isDemo || view !== "list") return [];
    const idSet = new Set(returnedPinIds);
    return calendarCelebrations.filter(
      (c) => idSet.has(c.id) && c.status === "upcoming",
    );
  }, [returnedPinIds, calendarCelebrations, isDemo, view]);

  const returnedNotInUpcomingList = useMemo(() => {
    const listIds = new Set(celebrations.map((c) => c.id));
    return returnedCelebrations.filter((c) => !listIds.has(c.id));
  }, [returnedCelebrations, celebrations]);

  const filteredGroups = useMemo(() => {
    if (filter === "all" || filter === "skipped" || filter === "needs_checkout") {
      return groups;
    }
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((c) => {
          const stub = recommendationFor(c, today, recOpts);
          if (filter === "awaiting") return stub.statusKey === "ready";
          if (filter === "autopilot") return stub.statusKey === "autopilot";
          return false;
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, filter, today, recOpts]);

  // MS389: 3-way indicator. "All caught up" should mean *no action
  // needed right now*. Prior behaviour rendered "All caught up" any
  // time awaitingCount===0, which conflicted with a right-rail that
  // still showed an upcoming celebration (e.g. Daniel's birthday 41
  // days out, on AutoPilot) — buyer saw the green "all clear" badge
  // sitting next to a "Awaiting your nod" eyebrow on the same screen.
  //
  // New states:
  //   - 1+ awaiting nod (in the next 14 days)  → "N awaiting your nod" (coral)
  //   - 0 awaiting, 1+ on autopilot            → "On AutoPilot" (neutral)
  //   - 0 of either                            → "All caught up" (green)
  //
  // 14-day window: awaitingCount already uses recommendationFor's
  // statusKey === "ready" rule, which today fires at daysOut ≤ 1.
  // We also restrict to ≤14 days so a hypothetical engine change
  // that broadens "ready" doesn't suddenly include items two months
  // out in the buyer-attention bucket.
  const awaitingCount = celebrations.filter((c) => {
    const stub = recommendationFor(c, today, recOpts);
    if (stub.statusKey !== "ready") return false;
    return stub.daysOut <= 14;
  }).length;
  const autopilotCount = celebrations.filter((c) => {
    const stub = recommendationFor(c, today, recOpts);
    return stub.statusKey === "autopilot" || stub.statusKey === "drafting";
  }).length;

  const attentionCounts = useMemo(
    () => ({
      awaitingNod: awaitingCount,
      needsCheckout: needsCheckoutQuery.data?.length ?? 0,
      paymentFailed: (ordersListQuery.data ?? []).filter(isPaymentFailedOrder).length,
      skipped: skippedQuery.data?.length ?? 0,
    }),
    [
      awaitingCount,
      needsCheckoutQuery.data,
      ordersListQuery.data,
      skippedQuery.data,
    ],
  );

  const celebrationsQuery =
    filter === "skipped"
      ? skippedQuery
      : filter === "needs_checkout"
        ? needsCheckoutQuery
        : upcomingQuery;
  const selectedIsLockedIn = selected?.status === "approved";
  const modalTarget = modalCelebration ?? selected;
  const modalSkipped = canRevitalizeCelebration(modalTarget);
  const modalLockedIn = modalTarget?.status === "approved";

  const closeApprovalModal = useCallback(() => {
    setModalOpen(false);
    setModalCelebration(null);
  }, []);

  const handleOpenCelebration = useCallback((cel: CelebrationRow) => {
    setSelectedId(cel.id);
    setModalCelebration(cel);
    setModalOpen(true);
  }, []);

  const dashboardClientActionHandlers = useMemo(() => {
    const findCelebrationById = (id: string): CelebrationRow | null => {
      const pools: CelebrationRow[] = [
        ...(upcomingQuery.data ?? []),
        ...calendarCelebrations,
        ...(skippedQuery.data ?? []),
        ...(needsCheckoutQuery.data ?? []).map(approvedCelebrationToRow),
      ];
      return pools.find((c) => c.id === id) ?? null;
    };
    return {
      setHomeView: (next: "list" | "calendar") => setView(next),
      openCelebration: (celebrationId: string) => {
        const cel = findCelebrationById(celebrationId);
        if (cel) {
          handleOpenCelebration(cel);
          return;
        }
        toast.error("Couldn't find that celebration on Home.");
      },
      setListFilter: (raw: string) => {
        const next = normalizeDashboardFilter(raw);
        if (next) setFilter(next);
      },
      setListRange: (raw: string) => {
        const next = normalizeDashboardListRange(raw);
        if (next) setListRange(next);
      },
      openBulkPeriod: ({
        period_key: _pk,
        celebration_ids,
      }: {
        period_key: string;
        celebration_ids: string[];
      }) => {
        void _pk;
        const firstId = celebration_ids[0];
        if (firstId) {
          const cel = findCelebrationById(firstId);
          if (cel) setSelectedId(cel.id);
        }
        setBulkModalOpen(true);
      },
    };
  }, [
    calendarCelebrations,
    handleOpenCelebration,
    needsCheckoutQuery.data,
    setFilter,
    setListRange,
    setView,
    upcomingQuery.data,
    skippedQuery.data,
    upcomingQuery.data,
  ]);

  useEffect(() => {
    if (isDemo) return;
    const viewParam = searchParams.get("view");
    if (viewParam === "list" || viewParam === "calendar") {
      setView(viewParam);
    }
    const openId = searchParams.get("open");
    if (openId) {
      const cel =
        (upcomingQuery.data ?? []).find((c) => c.id === openId) ??
        calendarCelebrations.find((c) => c.id === openId) ??
        (skippedQuery.data ?? []).find((c) => c.id === openId) ??
        (needsCheckoutQuery.data ?? []).map(approvedCelebrationToRow).find((c) => c.id === openId) ??
        null;
      if (cel) handleOpenCelebration(cel);
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      if (viewParam) next.delete("view");
      setSearchParams(next, { replace: true });
    }
  }, [
    calendarCelebrations,
    handleOpenCelebration,
    isDemo,
    needsCheckoutQuery.data,
    searchParams,
    setSearchParams,
    setView,
    skippedQuery.data,
    upcomingQuery.data,
  ]);

  const handleCompleteCheckoutForSelected = useCallback(async () => {
    if (!selected?.id) return;
    closeApprovalModal();
    await runCelebrationCheckout(selected.id);
  }, [closeApprovalModal, runCelebrationCheckout, selected?.id]);

  const handleSend = async (
    rec: ApprovalRecommendation,
  ): Promise<void | { redirected: true } | { openedCheckout: true }> => {
    if (isDemo) {
      toast.info("Demo mode: sign in to confirm delivery and checkout.");
      return { openedCheckout: true };
    }
    if (!selected || !companyId) return;

    if (selected.status === "approved" || selected.status === "ordered") {
      throw new Error("This celebration is already locked in.");
    }

    const tm = selected.team_members;
    const tmFlat = Array.isArray(tm) ? tm[0] : tm;
    if (!tmFlat?.id) {
      throw new Error("Couldn't resolve the recipient for this celebration.");
    }

    const result = await openRecommendedCelebrationCheckout({
      recommendation: rec,
      celebrationId: selected.id,
      celebrationDate: selected.date,
      celebrationType: selected.type,
      recipientId: tmFlat.id,
      recipientName: tmFlat.name ?? "Team member",
      dietaryRequirements: tmFlat.dietary_requirements ?? undefined,
    });

    let openedCheckout = false;
    applyCelebrationCheckoutResult(result, {
      onCheckout: (sku, ctx) => {
        closeApprovalModal();
        const defaultAddress = pickDefaultCompanyAddress(companyAddressesQuery.data);
        setCelebrationCheckout({
          ...ctx,
          defaultAddress: defaultAddress ?? undefined,
        });
        handleOpenConfirmSheet(sku);
        openedCheckout = true;
      },
      onPickGift: (ctx) => {
        closeApprovalModal();
        setDrawerRecipient({
          id: ctx.recipientId,
          name: ctx.recipientName,
          occasion: ctx.occasion,
          celebrationId: ctx.celebrationId,
          celebrationDate: ctx.defaultDate,
          dietaryRequirements: ctx.dietaryRequirements,
        });
      },
      onError: (message) => {
        throw new Error(message);
      },
    });

    // MS601: the dashboard's approval funnel event — fired only on a
    // SUCCESSFUL send (the buyer approved the ApprovalModal recommendation
    // and we proceeded to checkout or gift-pick; failures throw above and
    // demo/guards return early). PII-free: a celebration id + a line-item
    // count (1 when a SKU resolved on the rec, else 0) — never recipient
    // names / gift details. Symmetric with DASHBOARD_CELEBRATION_SKIPPED.
    // (Re-wired after the MS575 OrderConfirmSheet migration dropped it.)
    track(EVENTS.DASHBOARD_GIFT_APPROVED, {
      celebration_id: selected.id,
      line_item_count: rec.skuId ? 1 : 0,
    });

    if (openedCheckout) {
      return { openedCheckout: true };
    }
  };

  // MS82 → MS575: ApprovalModal's "Adjust something" button.
  //
  // Pre-MS575: closed the modal + navigated to /chat?recipientId=...&
  // celebration_id=...&occasion=... via celebrationToChatUrl. That hop
  // bounced the buyer off the dashboard entirely — they lost their
  // selected celebration, the up-next list scroll position, every
  // open tooltip, every filter pill. Coming back via the browser back
  // button re-mounted the whole page, lost in-flight UI state.
  //
  // Post-MS575: closes the modal + opens DashboardConciergeDrawer
  // with the recipient context pre-loaded. The drawer overlays the
  // dashboard rather than replacing it — close the drawer and the
  // buyer is exactly where they left off. The drawer's own draft
  // persistence (MS573) means an unfinished adjustment thread also
  // restores on next open, scoped by (user_id, recipient_id). The
  // standalone /chat route stays unchanged for cold visitors landing
  // from the homepage; it's still the canonical anon chat entry.
  //
  // Sibling surfaces (Calendar, Notifications, Orders) still navigate
  // to /chat via celebrationToChatUrl — those flows don't share the
  // ApprovalModal → "Adjust" entry point and their own UX context
  // (lists / detail rows) isn't as fragile to a navigation hop. They
  // can migrate independently if/when a similar drawer pattern lands
  // there. celebrationToChatUrl import retained: even though Dashboard
  // no longer calls it directly, the v2 follow-up handoff paths reuse
  // the same helper, and other surfaces import it too.
  //
  // Defensive fallback: if the selected celebration can't be unwrapped
  // to a team_member.id (very rare; the FK join would have to misbehave),
  // we fall back to /calendar so the buyer has SOME path forward. This
  // matches the pre-MS575 behaviour for the same edge case.
  const modalAdjustRecipient = useMemo((): DashboardConciergeDrawerRecipient | null => {
    if (isDemo || !selected) return null;
    const tm = selected.team_members;
    const memberRaw = Array.isArray(tm) ? tm[0] : tm;
    if (!memberRaw?.id) return null;
    return {
      id: memberRaw.id,
      name: memberRaw.name,
      occasion: selected.type,
      celebrationId: selected.id,
      celebrationDate: selected.date,
      dietaryRequirements: memberRaw.dietary_requirements ?? undefined,
    };
  }, [isDemo, selected]);

  const { setCelebrationContext, registerDashboardClientActions } =
    useFloatingConciergeUi();
  const { openGeneric: openFloatingConciergeGeneric } = useOpenAuthedConcierge();

  useEffect(() => {
    registerDashboardClientActions(dashboardClientActionHandlers);
    return () => registerDashboardClientActions(null);
  }, [dashboardClientActionHandlers, registerDashboardClientActions]);

  useEffect(() => {
    if (!modalOpen || !modalAdjustRecipient) return;
    setCelebrationContext({
      recipientId: modalAdjustRecipient.id,
      recipientName: modalAdjustRecipient.name,
      occasion: modalAdjustRecipient.occasion,
      celebrationId: modalAdjustRecipient.celebrationId,
      celebrationDate: modalAdjustRecipient.celebrationDate,
    });
  }, [modalOpen, modalAdjustRecipient, setCelebrationContext]);

  const handleRevitalize = useCallback(async () => {
    const cel = modalCelebration ?? selected;
    if (isDemo || !cel || !canRevitalizeCelebration(cel)) return;
    const celId = cel.id;
    const tm = cel.team_members;
    const tmFlat = Array.isArray(tm) ? tm[0] : tm;
    const memberName = tmFlat?.name ?? "Team member";

    const { error } = await supabase
      .from("celebrations")
      .update({ status: "upcoming" })
      .eq("id", celId);
    if (error) {
      throw new Error("Couldn't restore that celebration. Try once more?");
    }
    void queryClient.invalidateQueries({ queryKey: ["celebrations"] });
    setModalCelebration({ ...cel, status: "upcoming" });
    pinReturnedToSchedule(celId);
    setReturnedPinIds(getReturnedToScheduleIds());
    if (filter === "skipped") {
      setFilter("all");
    }
    toast.success(
      `${memberName}'s celebration is back on the schedule. Finish it from the list or keep going here.`,
    );
  }, [filter, isDemo, modalCelebration, queryClient, selected, setFilter]);

  const handleSkip = useCallback(
    async (_rec: ApprovalRecommendation) => {
      if (isDemo || !selected) {
        closeApprovalModal();
        return;
      }
      // Optimistically close so the modal feels responsive.
      closeApprovalModal();
      const skippedId = selected.id;
      const tm = selected.team_members;
      const tmFlat = Array.isArray(tm) ? tm[0] : tm;
      const memberName = tmFlat?.name ?? "Team member";
      const typeLabel = humanType(selected.type);

      try {
        await assertCelebrationCanBeSkipped(skippedId);
      } catch (blockErr) {
        const msg =
          blockErr instanceof Error
            ? blockErr.message
            : "Can't skip while a gift is in progress.";
        toast.error(msg);
        return;
      }

      const { error: skipErr } = await supabase
        .from("celebrations")
        .update({ status: "skipped" })
        .eq("id", skippedId);
      if (skipErr) {
        // MS601: capture the failed status flip. The user sees the
        // existing error toast (unchanged); Sentry now also sees the
        // Postgres/RLS error (codes/hints, no recipient PII) so a skip
        // regression doesn't stay invisible behind the toast.
        console.warn("[dashboard] skip failed:", skipErr);
        Sentry.captureException(skipErr, {
          tags: { scope: "dashboard_skip" },
        });
        toast.error("Couldn't skip that celebration. Try once more?");
        return;
      }

      try {
        await cancelOpenOrdersForCelebration(skippedId);
      } catch (orderErr) {
        console.warn("[dashboard] skip order cancel failed:", orderErr);
        Sentry.captureException(orderErr, {
          tags: { scope: "dashboard_skip_orders" },
        });
      }

      // MS84: invalidate the celebrations cache prefix so the
      // skipped row disappears from the Dashboard list immediately.
      // Without this the user closes the modal but still sees the
      // celebration in the up-next list until staleTime triggers a
      // refetch — feels like "did Skip do anything?".
      void queryClient.invalidateQueries({ queryKey: ["celebrations"] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      // If the just-skipped row was the selected one, drop the
      // selection so the detail pane doesn't keep painting a
      // recommendation for a celebration we just removed.
      if (selectedId === skippedId) {
        setSelectedId(null);
      }
      // MS85: mirror Calendar's skip flow — insert a notification row
      // so the user has a paper trail of what they skipped (and can
      // un-skip from /calendar). Also surface a toast for immediate
      // feedback. Notification insert is best-effort: a failure
      // doesn't undo the skip, just leaves the audit log gappy.
      if (user) {
        const { error: notifErr } = await supabase
          .from("notifications")
          .insert({
            user_id: user.id,
            title: `${memberName}'s ${typeLabel} skipped`,
            message: "We've set this one aside. You can bring it back any time from the Skipped filter on your dashboard.",
            type: "celebration",
            related_id: skippedId,
            related_type: "celebration",
          });
        if (!notifErr) {
          void queryClient.invalidateQueries({ queryKey: ["notifications", user.id] });
          window.dispatchEvent(new Event("lembra:notifications-read"));
        } else {
          console.warn("[dashboard] skip notification insert failed:", notifErr);
        }
      }
      // MS601: the negative-space counterpart to dashboard_gift_approved
      // — fired only after the status flip persisted. PII-free (the
      // celebration id only). The best-effort notification insert above
      // doesn't gate this: the skip itself already succeeded.
      track(EVENTS.DASHBOARD_CELEBRATION_SKIPPED, {
        celebration_id: skippedId,
      });
      toast(`We've set ${memberName}'s celebration aside. Bring it back any time from the Skipped filter.`);
    },
    [closeApprovalModal, isDemo, queryClient, selected, selectedId, user],
  );

  // MS45: handleSignOut moved to ConciergeLayout (sign-out lives in
  // the sidebar, not in any individual page).

  return (
    <>
      {/* MS45: Dashboard now renders ONLY its main content (list +
          detail panes). The chrome (white sidebar with logo/nav/
          sign-out/AutoPilot card) is owned by ConciergeLayout. */}
      {/* MS249: responsive grid. Pre-MS249 this was a hardcoded
          `gridTemplateColumns: "1fr 360px"` with NO breakpoint —
          on tablet widths the right rail squashed text in the
          list pane; on ultra-wide monitors the 1fr left pane
          stretched the buttons + rows to absurd widths ("bleeds
          in and out of frame" per Lucky). Now:
            mobile (sm and below): single column, detail pane
              stacks below the list (or hides if no selection).
            md+: original 2-col split with the right rail.
            xl+: bound the left column with max-width so wide
              monitors don't gas-stretch every row + button.

          MS582: ≥1920px push-drawer mode. When the buyer opens the
          DashboardConciergeDrawer on a desktop monitor, the right
          360px detail rail is REPLACED by the 500px drawer column
          (list + drawer, no detail pane). Below 1920px the drawer
          opens as a portal Sheet overlay and the grid is unchanged.
          The grid template flips via inline style — Tailwind's
          arbitrary value system can't express "depends on a JS
          boolean" cleanly, but the inline override is small + the
          alternative (className concat) would force the engine to
          purge two near-identical class strings.

          Visual cue: at <1920 there's a clear "click-to-overlay"
          mental model; at ≥1920 there's enough horizontal budget
          that the workbench feels right (list visible + drawer
          inline, both addressable). The detail pane disappears
          because the drawer IS the detail in this mode — both
          surface the same recipient context and showing both
          would duplicate the information. */}
      <div
        className="grid min-h-screen grid-cols-1 md:grid-cols-[minmax(0,1fr)_360px]"
        style={
          usePushDrawer
            ? { gridTemplateColumns: "minmax(0,1fr) auto" }
            : undefined
        }
        data-drawer-layout={usePushDrawer ? "push" : "overlay"}
      >
          {/* List pane — MS269: inner max-w wrapper so on ultra-wide
              monitors the headline + filter pills + celebration rows
              don't stretch to absurd widths. Same MS253 PageShell
              spirit (Lucky's "everything stretches"), just applied
              inside the existing split-grid structure rather than
              wrapping the whole page. */}
          <div className="border-r bg-white py-8 min-w-0" style={{ borderColor: "rgba(29,52,97,0.08)" }}>
            <div className="mx-auto max-w-3xl 2xl:max-w-5xl">
            {/* MS378: inline SmartIntentBar / InlineConcierge surface
                removed. Chat now lives exclusively in the bottom-right
                FloatingConcierge widget which appears on /dashboard
                (and every other authed tab). The "Up next" headline
                is now the first thing the user sees on the page,
                kept clear of the chat-surface real-estate the inline
                bar used to occupy. The widget's coral pill is one
                click away in the corner for anyone who wants chat. */}

            {/* MS389: AutoPilot ON + no card saved → coral warning.
                Renders only when both queries have resolved (see
                showAutopilotNoCardWarning) so it can't briefly flash
                on a first paint where the saved card hasn't loaded
                yet. Non-dismissible by design — the user's next
                scheduled celebration genuinely won't fire without a
                card, so the banner sticks until they add one. The
                Settings sister-guard MS388 is bringing will stop a
                fresh user from entering this state in the first
                place; this banner handles existing users + the gap
                window where MS388 hasn't shipped. */}
            {showAutopilotNoCardWarning && (
              <div className="mx-8 mb-4">
                <div
                  className="flex items-start gap-3 rounded-lg border px-4 py-3"
                  role="alert"
                  style={{
                    background: "rgba(196,86,74,0.06)",
                    borderColor: "rgba(196,86,74,0.28)",
                  }}
                >
                  <span
                    aria-hidden
                    className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white"
                    style={{ background: "#C4564A" }}
                  >
                    !
                  </span>
                  <div className="min-w-0 flex-1 text-[12.5px] leading-[1.5]" style={{ color: "rgba(29,52,97,0.92)" }}>
                    <strong style={{ color: "#A84539" }}>AutoPilot is on but no card is saved.</strong>{" "}
                    Your next scheduled celebration won't fire.{" "}
                    <Link
                      to="/settings/payment"
                      className="font-semibold underline underline-offset-2"
                      style={{ color: "#C4564A" }}
                    >
                      Add a card →
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {showAutopilotTrustGateBanner && (
              <div className="mx-8 mb-4">
                <div
                  className="flex items-start gap-3 rounded-lg border border-charcoal/10 bg-butter/30 px-4 py-3 text-[12.5px] leading-[1.5] text-navy/90"
                  role="status"
                >
                  <span className="font-semibold text-navy">
                    {manualApprovalCount} of {MANUAL_APPROVALS_FOR_AUTOPILOT} manual approvals.
                  </span>
                  <span className="text-charcoal/70">
                    Approve {manualApprovalRemaining} more on the dashboard to unlock AutoPilot in{" "}
                    <Link to="/settings/autopilot" className="font-semibold text-coral underline underline-offset-2">
                      Settings
                    </Link>
                    .
                  </span>
                </div>
              </div>
            )}

            {/* MS556: pool-status widget. Sits between the AutoPilot
                warning (urgent action banner) and the "Up next" headline
                so the buyer's first scan reads "where am I on this
                period's spend" before they look at the celebration list.
                Hides itself entirely when pool_cadence is per_celebration
                or null (unconfigured) — non-budget users see the same
                dashboard as pre-MS556. Demo path skips this; demo seed
                has no companies row to read pool_cadence from. */}
            {!isDemo && (
              <DashboardPoolWidget
                poolCadence={companySettingsQuery.data?.pool_cadence}
                companyId={companyId}
                createdBy={profileQuery.data?.id ?? null}
              />
            )}

            {/* MS559: first-time budget nudge. Occupies the same slot
                as the pool widget but only renders when pool_cadence is
                null (never configured) — so a brand-new company sees the
                nudge, a configured company sees the widget, and a
                per_celebration company sees neither. Dismissible; once
                dismissed it stays gone (localStorage). Demo skips it for
                the same reason the widget does — no companies row.

                Cohesion guard: suppress this dismissible discovery nudge
                whenever an AutoPilot banner is already showing, so the
                low-priority nudge never stacks beneath a genuine
                action-required / trust-progress strip above the "Up next"
                heading (which would push the list below the fold). The
                always-available Settings nav + Settings budget empty-state
                remain as discovery paths in that case. */}
            {!isDemo &&
              !showAutopilotNoCardWarning &&
              !showAutopilotTrustGateBanner && (
              <DashboardBudgetNudge
                poolCadence={companySettingsQuery.data?.pool_cadence}
              />
            )}

            <div className="flex items-end justify-between px-8 pb-4 pt-2">
              <div>
                {/* MS9, converted from DM Serif Display to Newsreader
                    with italic-emphasis on "next" in coral. Matches
                    the system pattern used across landing + Phase 2
                    pages (About, Contact) + onboarding. Conservative
                    polish, Dashboard is task-focused, doesn't want
                    a marketing-style hero on top of working data. */}
                <h1
                  className="text-3xl font-bold text-navy"
                  style={{
                    fontFamily: "Newsreader, Georgia, serif",
                    fontWeight: 500,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.1,
                  }}
                >
                  Up{" "}
                  <em
                    style={{
                      fontStyle: "italic",
                      fontWeight: 500,
                      color: "#C4564A",
                    }}
                  >
                    next
                  </em>
                </h1>
                <div
                  className="mt-1 flex flex-wrap items-center gap-2 text-sm tabular-nums"
                  style={{ color: "rgba(29,52,97,0.55)" }}
                >
                  <span>
                    {view === "calendar"
                      ? `${celebrations.length} celebration${celebrations.length === 1 ? "" : "s"} · all dates`
                      : `${celebrations.length} celebration${celebrations.length === 1 ? "" : "s"} · ${listRangeMeta.subtitle}`}
                  </span>
                  {view === "list" && !isDemo && (
                    <div
                      className="flex gap-0.5 rounded-md border p-0.5"
                      style={{ borderColor: "rgba(29,52,97,0.14)" }}
                      role="group"
                      aria-label="List date range"
                    >
                      {DASHBOARD_LIST_RANGE_OPTIONS.map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setListRange(key)}
                          className="rounded-[5px] px-2 py-0.5 text-[10px] font-semibold transition-colors"
                          style={{
                            background:
                              listRange === key ? "rgba(29,52,97,0.92)" : "transparent",
                            color: listRange === key ? "#FFF8E7" : "rgba(29,52,97,0.55)",
                          }}
                          aria-pressed={listRange === key}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs tabular-nums">
                {/* MS389: 3-way state replaces the old binary
                    awaitingCount/all-caught-up split. See the
                    awaitingCount comment for why "All caught up"
                    was misleading on its own. Visual treatments:
                      awaiting > 0 → coral, strong (calls for action)
                      autopilot > 0 → neutral navy (informational)
                      everything else → soft green (true zero state) */}
                {awaitingCount > 0 ? (
                  <strong style={{ color: "#C4564A", fontWeight: 600 }}>
                    {awaitingCount} awaiting your nod
                  </strong>
                ) : autopilotCount > 0 ? (
                  <span style={{ color: "rgba(29,52,97,0.55)" }}>On AutoPilot</span>
                ) : (
                  <span style={{ color: "#5B8C76" }}>All caught up</span>
                )}
                {/* List | Calendar view toggle */}
                <div
                  className="flex gap-0.5 rounded-md border p-0.5"
                  style={{ borderColor: "rgba(29,52,97,0.14)" }}
                  role="tablist"
                  aria-label="Switch view"
                >
                  {(
                    [
                      ["list", "List"],
                      ["calendar", "Calendar"],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      role="tab"
                      aria-selected={view === key}
                      onClick={() => setView(key)}
                      className="rounded-[5px] px-2.5 py-1 text-[11px] font-semibold transition-colors"
                      style={{
                        background: view === key ? "rgba(29,52,97,0.92)" : "transparent",
                        color: view === key ? "#FFF8E7" : "rgba(29,52,97,0.55)",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {view === "list" && !isDemo && (
              <DashboardAttentionStrip
                counts={attentionCounts}
                activeFilter={filter}
                onSelectFilter={setFilter}
                onPaymentFailedClick={() => navigate("/orders?tab=active")}
              />
            )}

            {view === "list" &&
              !isDemo &&
              isBulkProgramActive(bulkProgramQuery.data ?? null) &&
              (bulkRunQuery.data?.headcount ?? 0) > 0 && (
                <div className="mx-8 mb-3 rounded-xl border border-coral/20 bg-coral/5 px-4 py-3 text-sm text-navy">
                  <span className="font-medium">Bulk period — {bulkRunQuery.data?.periodLabel}</span>
                  {": "}
                  {bulkRunQuery.data?.headcount}{" "}
                  {bulkRunQuery.data?.headcount === 1 ? "person" : "people"}
                  {bulkRunQuery.data?.run?.recommendation_id
                    ? " · ready to checkout"
                    : " · approve shared dessert"}
                  <button
                    type="button"
                    className="ml-2 font-medium underline"
                    onClick={() => setBulkModalOpen(true)}
                  >
                    Open roster
                  </button>
                </div>
              )}

            {view === "list" && (
              <div className="flex gap-1 border-b px-8 pb-3.5" style={{ borderColor: "rgba(29,52,97,0.08)" }}>
                {(
                  [
                    ["all", "All"],
                    ["awaiting", "Awaiting your nod"],
                    ["autopilot", "On AutoPilot"],
                    ["needs_checkout", "Needs checkout"],
                    ["skipped", "Skipped"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setFilter(key)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      filter === key ? "bg-[rgba(29,52,97,0.06)] text-navy" : "text-[rgba(29,52,97,0.55)] hover:bg-[rgba(29,52,97,0.03)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {/* Calendar view */}
            {view === "calendar" && (
              <CalendarView
                celebrations={celebrations}
                selectedId={selected?.id ?? null}
                onSelect={(c) => setSelectedId(c.id)}
                onOpen={handleOpenCelebration}
                today={today}
              />
            )}

            {/* MS87: error state for celebrations fetch failure. Was
                missing — a failed upcomingQuery showed the loading
                skeleton forever, leaving the buyer no signal that
                something went wrong + no way to retry. Mirrors the
                pattern Calendar / Orders / Notifications / Team have
                used since Sprint 2.5a. Only render in non-demo mode
                (demo seed never errors). */}
            {view === "list" && celebrationsQuery.isError && !isDemo && (
              <div className="px-8 py-8">
                <ErrorCard
                  title="Couldn't load your celebrations"
                  onRetry={() => celebrationsQuery.refetch()}
                />
              </div>
            )}

            {/* List body */}
            {view === "list" && !celebrationsQuery.isError && (celebrationsQuery.isLoading && !isDemo ? (
              <ListLoading />
            ) : filteredGroups.length === 0 && returnedNotInUpcomingList.length === 0 ? (
              <EmptyState filter={filter} onAskConcierge={openFloatingConciergeGeneric} />
            ) : (
              <>
              {returnedNotInUpcomingList.length > 0 && (
                <div className="px-8 pb-2 pt-4">
                  <div className="mb-2">
                    <p
                      className="text-[10px] font-medium uppercase tracking-[0.12em]"
                      style={{
                        fontFamily: "JetBrains Mono, monospace",
                        color: "rgba(29,52,97,0.45)",
                      }}
                    >
                      Returned to schedule
                    </p>
                    <p className="mt-1 text-[12px]" style={{ color: "rgba(29,52,97,0.55)" }}>
                      You brought these back. Open to send, adjust, or skip.
                    </p>
                  </div>
                  {returnedNotInUpcomingList.map((c) => {
                    const stub = recommendationFor(c, today, recOpts);
                    const isSelected = selected?.id === c.id;
                    return (
                      <button
                        key={`returned-${c.id}`}
                        type="button"
                        onClick={() => handleOpenCelebration(c)}
                        className={`mb-1 grid w-full grid-cols-[110px_1fr_auto] items-center gap-3 rounded-md border border-dashed py-2.5 pr-2 text-left transition-colors 2xl:grid-cols-[130px_1fr_auto] 2xl:gap-4 2xl:py-3.5 ${
                          isSelected
                            ? "border-[rgba(29,52,97,0.2)] bg-[rgba(29,52,97,0.06)]"
                            : "border-[rgba(29,52,97,0.12)] hover:bg-[rgba(29,52,97,0.03)]"
                        }`}
                      >
                        <span
                          className="pr-1 text-right text-[11px] 2xl:text-xs"
                          style={{
                            fontFamily: "JetBrains Mono, monospace",
                            color: "rgba(29,52,97,0.35)",
                          }}
                        >
                          {stub.timeLabel}
                        </span>
                        <span className="flex min-w-0 items-center gap-2.5 2xl:gap-3">
                          <span
                            className="h-1.5 w-1.5 flex-shrink-0 rounded-full 2xl:h-2 2xl:w-2"
                            style={{ background: typeColor(c.type) }}
                          />
                          <span
                            className="truncate text-[13.5px] font-medium 2xl:text-base"
                            style={{ color: "rgba(29,52,97,0.92)" }}
                          >
                            {memberName(c)}
                          </span>
                          <span
                            className="ml-1 truncate text-[12.5px] 2xl:text-sm"
                            style={{ color: "rgba(29,52,97,0.55)" }}
                          >
                            · {humanType(c.type)}
                          </span>
                        </span>
                        <StatusPill kind={stub.statusKey} label={stub.statusLabel} />
                      </button>
                    );
                  })}
                </div>
              )}
              {filteredGroups.map((g) => (
                <div key={g.dayKey} className="px-8 pb-1 pt-4">
                  <div className="mb-2 flex items-baseline gap-3">
                    <span
                      className="w-[110px] text-[11px] font-medium uppercase tracking-[0.06em] 2xl:w-[130px] 2xl:text-xs"
                      style={{ fontFamily: "JetBrains Mono, monospace", color: "rgba(29,52,97,0.35)" }}
                    >
                      {g.dayLabel}
                    </span>
                    <span className="text-[11px] 2xl:text-xs" style={{ color: "rgba(29,52,97,0.55)" }}>
                      {g.relLabel}
                    </span>
                  </div>
                  {g.items.map((c) => {
                    const stub = recommendationFor(c, today, recOpts);
                    const isSelected = selected?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => handleOpenCelebration(c)}
                        className={`grid w-full grid-cols-[110px_1fr_auto] items-center gap-3 rounded-md py-2.5 pr-2 text-left transition-colors 2xl:grid-cols-[130px_1fr_auto] 2xl:gap-4 2xl:py-3.5 ${
                          isSelected ? "bg-[rgba(29,52,97,0.05)]" : "hover:bg-[rgba(29,52,97,0.025)]"
                        }`}
                      >
                        <span
                          className="pr-1 text-right text-[11px] 2xl:text-xs"
                          style={{ fontFamily: "JetBrains Mono, monospace", color: "rgba(29,52,97,0.35)" }}
                        >
                          {stub.timeLabel}
                        </span>
                        <span className="flex min-w-0 items-center gap-2.5 2xl:gap-3">
                          <span
                            className="h-1.5 w-1.5 flex-shrink-0 rounded-full 2xl:h-2 2xl:w-2"
                            style={{ background: typeColor(c.type) }}
                          />
                          <span className="truncate text-[13.5px] font-medium 2xl:text-base" style={{ color: "rgba(29,52,97,0.92)" }}>
                            {memberName(c)}
                          </span>
                          <span className="ml-1 truncate text-[12.5px] 2xl:text-sm" style={{ color: "rgba(29,52,97,0.55)" }}>
                            · {humanType(c.type)}
                          </span>
                        </span>
                        <StatusPill kind={stub.statusKey} label={stub.statusLabel} />
                      </button>
                    );
                  })}
                </div>
              ))}
              </>
            ))}
            </div>{/* /max-w-3xl */}
          </div>

          {/* Second column — MS582: this slot is either the legacy
              detail pane (default) or the in-grid push-drawer column
              (≥1920px with drawer open). The detail pane and the
              drawer both render to roughly the same horizontal slot,
              so they're MUTUALLY EXCLUSIVE — showing both would
              double up the recipient context (the detail pane shows
              the selected celebration's recommendation; the drawer
              shows the same recipient's adjust-chat). */}
          {usePushDrawer ? (
            <DashboardConciergeDrawer
              recipient={drawerRecipient}
              onClose={() => setDrawerRecipient(null)}
              mode="push"
            />
          ) : (
            <div className="overflow-y-auto px-6 py-8 min-w-0 border-t md:border-t-0" style={{ background: "#FAF7F0", borderColor: "rgba(29,52,97,0.08)" }}>
              {/* Detail pane — MS249: also gets min-w-0 to opt out of
                  grid's default min-content sizing which lets long
                  vendor names + product summaries shove the column wider
                  than the rail allocation. Border-top on mobile gives
                  visual separation when stacked under the list. */}
              {!selected ? (
                <DetailEmpty
                  greetingLine={greetingLine}
                  recentGiftRequests={recentGiftRequests}
                  savedGifts={wishlist.slice(0, 3)}
                  onOpenOrder={(orderId) => navigate(`/orders/${orderId}`)}
                  onAskConcierge={openFloatingConciergeGeneric}
                  // MS390 Bug #16: the dedicated "gifts" tab on /orders
                  // was collapsed into the unified order list. Route to
                  // /orders (which now shows all orders chronologically)
                  // — the per-card emoji + category badge still makes
                  // chat-originated orders visually distinct in the list.
                  onSeeAllOrders={() => navigate("/orders")}
                  onSeeAllSaved={() => navigate("/wishlist")}
                  /* MS64a: instead of bouncing each saved-gift tile
                     to /chat, lift the View action to the parent so
                     the canonical ProductDetailModal renders here. */
                  onViewSavedGift={(row) => {
                    const skuCardData = wishlistRowToSkuCardData(row);
                    if (skuCardData) savedGiftsCheckout.handleViewCard(skuCardData);
                  }}
                />
              ) : recommendation ? (
                <DetailPane
                  rec={recommendation}
                  lockedIn={selectedIsLockedIn}
                  skipped={canRevitalizeCelebration(selected)}
                  onOpen={() => selected && handleOpenCelebration(selected)}
                  onRevitalize={
                    canRevitalizeCelebration(selected)
                      ? () => handleRevitalize()
                      : undefined
                  }
                />
              ) : null}
            </div>
          )}
        </div>

      <ApprovalModal
        open={modalOpen}
        recommendation={recommendation}
        onClose={closeApprovalModal}
        onSend={handleSend}
        // MS82: wire previously-dead Adjust + Skip affordances.
        // Adjust → /chat with celebration context pre-composed.
        // Skip → flip celebration status="skipped".
        adjustRecipient={modalAdjustRecipient}
        adjustUserId={user?.id ?? null}
        onSkip={handleSkip}
        onRevitalize={handleRevitalize}
        celebrationSkipped={modalSkipped}
        // Live dashboard: Send opens OrderConfirmSheet (address, notes,
        // delivery date) before submit-gift-request + Stripe.
        // Demo skips persistence but keeps optimistic success copy.
        intentOnly={isDemo}
        lockedIn={modalLockedIn}
        onCompleteCheckout={
          !isDemo && selected?.id ? handleCompleteCheckoutForSelected : undefined
        }
        successDismissLabel="Back to Home"
      />

      <BulkCelebrationModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        companyId={companyId ?? ""}
        onOpenCheckout={(sku, seed) => {
          setBulkCheckout({
            ...seed,
            defaultAddress:
              seed.defaultAddress ??
              pickDefaultCompanyAddress(companyAddressesQuery.data) ??
              undefined,
          });
          setCelebrationCheckout(null);
          handleOpenConfirmSheet(sku);
        }}
      />

      <ProductCheckoutModals
        flow={savedGiftsCheckout}
        recipientName={
          bulkCheckout?.recipientLabel ?? celebrationCheckout?.recipientName ?? ""
        }
        recipientId={celebrationCheckout?.recipientId ?? null}
        occasion={bulkCheckout ? "bulk_period" : celebrationCheckout?.occasion ?? "other"}
        defaultDate={bulkCheckout?.defaultDate ?? celebrationCheckout?.defaultDate}
        defaultAddress={
          bulkCheckout?.defaultAddress ??
          celebrationCheckout?.defaultAddress ??
          pickDefaultCompanyAddress(companyAddressesQuery.data) ??
          undefined
        }
        celebrationId={celebrationCheckout?.celebrationId ?? null}
        bulkCheckout={
          bulkCheckout
            ? {
                bulkRunId: bulkCheckout.bulkRunId,
                bulkPeriodKey: bulkCheckout.bulkPeriodKey,
                celebrationIds: bulkCheckout.celebrationIds,
              }
            : null
        }
        isAnonymous={false}
        fulfillmentReturn={ordersHandoffReturn}
        onCheckoutClose={() => {
          setCelebrationCheckout(null);
          setBulkCheckout(null);
        }}
        onSubmitted={() => {
          void queryClient.invalidateQueries({ queryKey: ["celebrations"] });
          void queryClient.invalidateQueries({ queryKey: ["orders"] });
          void queryClient.invalidateQueries({ queryKey: ["bulk-run"] });
          void queryClient.invalidateQueries({
            queryKey: ["companies", companyId, "manual-approval-progress"],
          });
          setCelebrationCheckout(null);
          setBulkCheckout(null);
        }}
      />

      {/* MS572 + MS575: in-dashboard concierge drawer. Mounted at page
          root for pick-gift and legacy ?drawer= deep links; in-modal
          Adjust (phase 2c) no longer opens this drawer from Home.
          Dashboard state. `recipient=null` keeps the underlying Sheet
          closed; nothing user-visible until the Adjust button fires.
          MS582: this mount is the OVERLAY-MODE path only. When the
          viewport ≥ 1920px the drawer renders INSIDE the grid (above,
          in the second column slot) in push mode instead. We mount
          this overlay variant unconditionally so it can react to a
          window-resize-down event mid-open (push → overlay) without
          dropping state — but we set its `recipient` to null when in
          push mode so this overlay Sheet stays closed and the inline
          aside owns the rendering. */}
      <DashboardConciergeDrawer
        recipient={usePushDrawer ? null : drawerRecipient}
        onClose={() => setDrawerRecipient(null)}
        fulfillmentReturn={drawerRecipient ? ordersHandoffReturn : null}
        onDashboardClientActions={dashboardClientActionHandlers}
        mode="overlay"
      />
    </>
  );
}

// MS45: NavSection / NavItem moved to ConciergeLayout.

// ── Detail pane ──────────────────────────────────────────────────────

// MS249: DetailPane wired the previously-dead Adjust + Skip buttons
// down from the parent. MS389 then removed those buttons from the
// right rail entirely (they duplicated the same pair inside the
// ApprovalModal that "Open" launches), so this component is back to
// taking only `rec` + the `onOpen` CTA. handleAdjust/handleSkip in
// the parent still live and wire into ApprovalModal — that's the
// single canonical place to invoke them.
function DetailPane({
  rec,
  lockedIn = false,
  skipped = false,
  onOpen,
  onRevitalize,
}: {
  rec: ApprovalRecommendation;
  lockedIn?: boolean;
  skipped?: boolean;
  onOpen: () => void;
  /** One-click restore when viewing the Skipped filter — same action as ApprovalModal Revitalize. */
  onRevitalize?: () => void | Promise<void>;
}) {
  const [revitalizing, setRevitalizing] = useState(false);

  const handleRevitalizeClick = async () => {
    if (!onRevitalize || revitalizing) return;
    setRevitalizing(true);
    try {
      await onRevitalize();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Couldn't restore that celebration. Try once more?";
      toast.error(msg);
    } finally {
      setRevitalizing(false);
    }
  };

  const eyebrow = skipped
    ? "Skipped"
    : lockedIn
      ? "Needs checkout"
      : "Awaiting your nod";
  return (
    <>
      <div
        className="mb-2 text-[10px] font-medium uppercase"
        style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.12em", color: "rgba(29,52,97,0.55)" }}
      >
        {eyebrow} · {humanDaysOut(rec.daysOut)}
      </div>
      <h2
        className="mb-1"
        style={{
          fontFamily: "DM Serif Display, serif",
          fontSize: 24,
          lineHeight: 1.15,
          color: "rgba(29,52,97,1)",
          letterSpacing: "-0.01em",
        }}
      >
        {rec.recipientFirstName}'s {rec.occasion}
      </h2>
      {/* MS81: "10:00 · office" was hardcoded — the time was a lie
          (engine actually picks the delivery window via deliveryWindow
          input; default morning_early renders as "9 – 10 am") and
          "office" was a generic placeholder unrelated to whatever
          address the company has set. Replaced with the engine's
          actual deliveryDetail so the line reflects what'll happen.
          Full address is too long here and is surfaced in the
          ApprovalModal when the user opens the celebration. */}
      <div className="mb-6 text-[12.5px]" style={{ color: "rgba(29,52,97,0.55)" }}>
        {humanFullDate(rec.date)} · {rec.deliveryDetail}
      </div>

      <div className="mb-3.5 rounded-lg border bg-white p-4" style={{ borderColor: "rgba(29,52,97,0.08)" }}>
        {/* MS64e: was hardcoded "A dozen cupcakes from {rec.bakery}"
            which only made sense pre-pivot when every order was a
            cupcake order. The recommendation engine now picks from
            the full vendor catalogue (hampers, vouchers, flowers,
            experiences, cakes); using productSummary preserves the
            category-specific phrasing the engine already authored.
            bakery is appended so the sender knows where it's coming
            from without us re-deriving the "from X" phrasing. */}
        <SummaryRow label="Sending" value={
          <>
            <strong>{rec.productSummary}</strong>
            {rec.bakery ? <> from {rec.bakery}</> : null}
          </>
        } />
        <SummaryRow label="Card" value={rec.cardDetail} />
        <SummaryRow label="Total" value={<><strong>${(rec.totalCents / 100).toFixed(0)}</strong> · {tierLabel(rec.tier)} tier</>} />
      </div>

      {skipped && onRevitalize ? (
        <div className="mb-3.5 flex gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="flex flex-1 items-center justify-center rounded-lg border px-3 py-3 text-sm font-semibold transition-all hover:-translate-y-px"
            style={{
              borderColor: "rgba(29,52,97,0.14)",
              color: "rgba(29,52,97,0.92)",
              background: "#FFFFFF",
            }}
          >
            Open {rec.recipientFirstName}&apos;s celebration
          </button>
          <button
            type="button"
            onClick={() => void handleRevitalizeClick()}
            disabled={revitalizing}
            className="flex flex-1 items-center justify-center rounded-lg px-3 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:shadow-[0_6px_16px_-8px_rgba(196,86,74,0.45)] disabled:opacity-70"
            style={{ background: "#C4564A" }}
            onMouseEnter={(e) => {
              if (!revitalizing) e.currentTarget.style.background = "#A84539";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#C4564A";
            }}
          >
            {revitalizing
              ? "Bringing back…"
              : "Bring back to schedule"}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          className="mb-3.5 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-px hover:shadow-[0_6px_16px_-8px_rgba(196,86,74,0.45)]"
          style={{ background: lockedIn ? "#1D3461" : "#C4564A" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = lockedIn ? "#14253D" : "#A84539")}
          onMouseLeave={(e) => (e.currentTarget.style.background = lockedIn ? "#1D3461" : "#C4564A")}
        >
          {lockedIn
            ? `View ${rec.recipientFirstName}'s locked-in celebration`
            : `Open ${rec.recipientFirstName}'s celebration`}
        </button>
      )}

      <p className="border-t pt-3 text-[11.5px] leading-[1.55]" style={{ color: "rgba(29,52,97,0.55)", borderColor: "rgba(29,52,97,0.08)" }}>
        {skipped
          ? "You skipped this one. Bring them back with one tap, or open to review before sending."
          : lockedIn
          ? "Approved and saved. We'll reach out before charging your card."
          : <>We charge when you tap Send, using your saved card if you have one or secure checkout otherwise. The edit window closes {rec.editWindowCloses}. After that, AutoPilot sends the gift we&rsquo;ve prepared.</>}
      </p>
    </>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-t py-2 first:border-t-0 first:pt-0.5" style={{ borderColor: "rgba(29,52,97,0.08)" }}>
      <span
        className="w-16 flex-shrink-0 pt-0.5 text-[9.5px] font-medium uppercase"
        style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.06em", color: "rgba(29,52,97,0.35)" }}
      >
        {label}
      </span>
      <span className="flex-1 text-[12.5px] leading-[1.5]" style={{ color: "rgba(29,52,97,0.92)" }}>
        {value}
      </span>
    </div>
  );
}

function DetailEmpty({
  greetingLine,
  recentGiftRequests,
  savedGifts,
  onOpenOrder,
  onSeeAllOrders,
  onSeeAllSaved,
  onViewSavedGift,
  onAskConcierge,
}: {
  // Sprint 2.5a B.2 P2-5, pre-resolved greeting from the parent so
  // the rotation logic lives in one place and both render slots
  // (no-data state + has-data header) stay in sync within a session.
  // firstName was the previous-cycle prop; deprecated since this
  // component now receives the fully-formed greeting line.
  greetingLine: string;
  recentGiftRequests: OrderListRow[];
  // Sprint 2.2e, saved wishlist items for the secondary cluster.
  // Compact row format (top 3); link out to /wishlist for the full list.
  savedGifts: WishlistRow[];
  onOpenOrder: (id: string) => void;
  onSeeAllOrders: () => void;
  onSeeAllSaved: () => void;
  /** MS64a: lifted to the parent so the saved-gift tile opens
   *  ProductDetailModal at page scope rather than navigating to
   *  /chat. */
  onViewSavedGift: (row: WishlistRow) => void;
  onAskConcierge: () => void;
}) {
  // Sprint 1.7r: when the customer has no celebration selected, fall back
  // to showing their most-recent gift requests. Sprint 2.2e adds an
  // optional second cluster underneath for saved-but-not-yet-bought
  // gifts. If neither slot has data (true new account), the original
  // "pick a celebration" prompt holds.
  const hasOrders = recentGiftRequests.length > 0;
  const hasSaves = savedGifts.length > 0;

  if (!hasOrders && !hasSaves) {
    // MS86: "Pick a celebration on the left" was a lie when the
    // left list pane was ALSO empty (fresh account: no celebrations
    // yet, no orders, no saved gifts). The buyer would land here
    // and stare at two empty panes telling them to do impossible
    // things. Show actionable CTAs instead — same pair as the list-
    // pane EmptyState in MS72 (Ask the concierge → /chat, Set up
    // your team → /team), so the two panes finally agree on what
    // to do next.
    return (
      <div className="flex h-full flex-col items-center justify-center text-center px-8">
        <p className="mb-2 text-sm" style={{ color: "rgba(29,52,97,0.55)" }}>
          {greetingLine}
        </p>
        <p
          className="mb-5"
          style={{
            fontFamily: "Newsreader, Georgia, serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(29,52,97,0.92)",
          }}
        >
          Nothing on the calendar yet. Let's start one.
        </p>
        <p className="text-sm mb-6 max-w-md" style={{ color: "rgba(29,52,97,0.62)" }}>
          Send a one-off gift now, or set up your team so we can track
          birthdays + anniversaries for you going forward.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={onAskConcierge}
            className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white hover:bg-coral/85 transition-colors"
          >
            Ask the concierge →
          </button>
          <Link
            to="/team"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-charcoal/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral transition-colors"
          >
            Set up your team
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* MS64b crash fix: previous code read `Hi {firstName}.` but
          firstName was never destructured from props — DetailEmpty
          only receives greetingLine. Reuse the rotation-aware
          greeting line that the parent already resolved. */}
      <p className="mb-2 text-sm" style={{ color: "rgba(29,52,97,0.55)" }}>
        {greetingLine}
      </p>

      {hasOrders && (
        <>
          <p
            className="mb-5"
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontStyle: "italic",
              fontSize: 22,
              lineHeight: 1.2,
              color: "rgba(29,52,97,0.92)",
            }}
          >
            Your recent gifts.
          </p>

          <div className="mb-3 flex flex-col gap-2.5">
            {recentGiftRequests.map((order) => (
              <RecentGiftRow
                key={order.id}
                order={order}
                onClick={() => onOpenOrder(order.id)}
              />
            ))}
          </div>

          <button
            onClick={onSeeAllOrders}
            className="w-full rounded-md border bg-transparent px-3 py-2 text-xs hover:bg-[rgba(29,52,97,0.03)]"
            style={{ borderColor: "rgba(29,52,97,0.14)", color: "rgba(29,52,97,0.92)" }}
          >
            See all gift requests →
          </button>
        </>
      )}

      {hasSaves && (
        <>
          <p
            className="mb-3 mt-7"
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontStyle: "italic",
              fontSize: 18,
              lineHeight: 1.2,
              color: "rgba(29,52,97,0.92)",
            }}
          >
            Saved for later.
          </p>

          <div className="mb-3 flex flex-col gap-2">
            {savedGifts.map((row) => (
              <SavedGiftRow
                key={row.id}
                row={row}
                onView={() => onViewSavedGift(row)}
              />
            ))}
          </div>

          <button
            onClick={onSeeAllSaved}
            className="w-full rounded-md border bg-transparent px-3 py-2 text-xs hover:bg-[rgba(29,52,97,0.03)]"
            style={{ borderColor: "rgba(29,52,97,0.14)", color: "rgba(29,52,97,0.92)" }}
          >
            Open saved gifts →
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Sprint 2.2e, compact saved-gift row for the Dashboard right-pane
 * cluster. Smaller than RecentGiftRow because we're stacking two
 * clusters and want the second one to feel secondary. Click anywhere
 * on the row → open the saved SKU in /chat with a brief pre-composed.
 *
 * Stale rows (sku=null because the underlying vendor_product was
 * deleted) render an opaque "Item gone" tile so the customer can spot
 * + clean up via /wishlist. Click is no-op for those.
 */
function SavedGiftRow({
  row,
  onView,
}: {
  row: WishlistRow;
  /** MS64a: clicked tile now opens ProductDetailModal at the
   *  Dashboard page scope rather than navigating to /chat. The
   *  legacy chat-bridge is still available via the wishlist
   *  page's "Talk it through" secondary CTA. */
  onView: () => void;
}) {
  const sku = row.sku;
  const onClick = () => {
    if (!sku) return;
    onView();
  };

  if (!sku) {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border bg-white p-2.5 opacity-60"
        style={{ borderColor: "rgba(29,52,97,0.08)" }}
      >
        <div
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-sm"
          style={{ background: "rgba(29,52,97,0.06)" }}
          aria-hidden
        >
          🎁
        </div>
        <p className="flex-1 truncate text-[12px]" style={{ color: "rgba(29,52,97,0.55)" }}>
          Item no longer available
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 rounded-lg border bg-white p-2.5 text-left transition-all hover:-translate-y-px hover:shadow-[0_3px_10px_-6px_rgba(29,52,97,0.18)]"
      style={{ borderColor: "rgba(29,52,97,0.08)" }}
    >
      {/* Sprint 2.5a B.2-fix-1 (P0-2), render the actual product
          photography when image_url is present; fall back to the
          category emoji tile for SKUs without imagery (catalogue gap
          surfaced via /admin/catalogue-imagery). Same fallback grammar
          as /wishlist's SavedGiftCard so the two surfaces stay
          visually consistent. */}
      {sku.image_url ? (
        <div className="h-8 w-8 flex-shrink-0 overflow-hidden rounded-md bg-charcoal/5">
          <img
            src={sku.image_url}
            alt={`${sku.name}, ${sku.vendor_category} from ${sku.vendor_name}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      ) : (
        <div
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-sm"
          style={{ background: "rgba(247, 232, 164, 0.55)" }}
          aria-hidden
        >
          {categoryEmoji(sku.vendor_category)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[12.5px] font-medium"
          style={{ color: "rgba(29,52,97,0.92)" }}
        >
          {/* MS407: consistent with Browse/Chat/Wishlist — strip voucher tokens + vendor prefix */}
          {formatSkuName({ name: sku.name, vendor_name: sku.vendor_name, vendor_category: sku.vendor_category, is_voucher: sku.is_voucher ?? undefined })}
        </p>
        <p
          className="mt-0.5 truncate text-[11px]"
          style={{ color: "rgba(29,52,97,0.55)" }}
        >
          {sku.vendor_name} · ${Number(sku.price ?? 0).toFixed(0)}
        </p>
      </div>
    </button>
  );
}

function RecentGiftRow({
  order,
  onClick,
}: {
  order: OrderListRow;
  onClick: () => void;
}) {
  const parsed = parseGiftRequest(order);
  const occasion = occasionLabel(order.celebration_type);
  const paid = order.payment_status === "paid";
  return (
    <button
      onClick={onClick}
      className="group flex w-full items-start gap-3 rounded-lg border bg-white p-3 text-left transition-all hover:-translate-y-px hover:shadow-[0_4px_12px_-6px_rgba(29,52,97,0.18)]"
      style={{ borderColor: "rgba(29,52,97,0.08)" }}
    >
      <div
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-base"
        style={{ background: "rgba(247, 232, 164, 0.55)" }}
        aria-hidden
      >
        {categoryEmoji(parsed.category)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2">
          <span
            className="text-[10.5px] font-medium uppercase tracking-[0.06em]"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              color: "rgba(29,52,97,0.55)",
            }}
          >
            {categoryLabel(parsed.category)}
          </span>
          <span
            className="rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              letterSpacing: "0.04em",
              background: paid
                ? "rgba(91,140,118,0.12)"
                : "rgba(196,86,74,0.08)",
              color: paid ? "#5B8C76" : "#C4564A",
            }}
          >
            {paid ? "Paid" : (order.payment_status ?? "Pending")}
          </span>
        </div>
        <p
          className="truncate text-[13px] font-medium"
          style={{ color: "rgba(29,52,97,0.92)" }}
        >
          {parsed.product}
        </p>
        <p
          className="mt-0.5 truncate text-[11.5px]"
          style={{ color: "rgba(29,52,97,0.55)" }}
        >
          For {order.team_member_name}
          {order.celebration_type ? ` · ${occasion}` : ""}
          {order.delivery_date
            ? ` · ${format(new Date(order.delivery_date + "T00:00:00"), "d MMM")}`
            : ""}
        </p>
      </div>
    </button>
  );
}

// ── List primitives ──────────────────────────────────────────────────

function StatusPill({
  kind,
  label,
}: {
  kind: "ready" | "drafting" | "autopilot" | "done" | "locked_in" | "skipped";
  label: string;
}) {
  const styles = {
    ready: { bg: "rgba(196,86,74,0.08)", color: "#C4564A" },
    drafting: { bg: "rgba(107,124,156,0.1)", color: "#6B7C9C" },
    autopilot: { bg: "rgba(91,140,118,0.12)", color: "#5B8C76" },
    done: { bg: "rgba(107,124,156,0.1)", color: "#6B7C9C" },
    locked_in: { bg: "rgba(29,52,97,0.1)", color: "#1D3461" },
    skipped: { bg: "rgba(29,52,97,0.06)", color: "rgba(29,52,97,0.55)" },
  }[kind];
  return (
    <span
      className="rounded px-2 py-0.5 text-[10.5px] font-medium uppercase 2xl:px-2.5 2xl:py-1 2xl:text-xs"
      style={{
        fontFamily: "JetBrains Mono, monospace",
        letterSpacing: "0.04em",
        background: styles.bg,
        color: styles.color,
      }}
    >
      {label}
    </span>
  );
}

function ListLoading() {
  return (
    <div className="space-y-3 px-8 py-6">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-md" style={{ background: "rgba(29,52,97,0.05)" }} />
      ))}
    </div>
  );
}

function EmptyState({
  filter,
  onAskConcierge,
}: {
  filter: string;
  onAskConcierge: () => void;
}) {
  return (
    <div className="px-8 py-16 text-center">
      <p
        className="mb-3"
        style={{
          fontFamily: "Newsreader, Georgia, serif",
          fontStyle: "italic",
          fontSize: 22,
          color: "rgba(29,52,97,0.92)",
        }}
      >
        {filter === "all" ? "All quiet for now." : "Nothing matches this filter."}
      </p>
      {filter === "all" ? (
        <>
          {/* MS72: was a single "Set up your team" link — which is the
              right long-term path but assumes the visitor wants to
              schedule recurring celebrations. Plenty of first-time
              visitors land on an empty dashboard wanting to send ONE
              gift (welcome, farewell, thank-you) without setting up a
              team first. Surfacing /chat as an equally-weighted option
              gives them the direct one-off path without dropping the
              team setup as a secondary option. */}
          <p className="text-sm mb-4" style={{ color: "rgba(29,52,97,0.55)" }}>
            Send a one-off gift now, or set up your team so we can track
            the next round of birthdays and anniversaries for you.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={onAskConcierge}
              className="inline-flex items-center gap-1.5 rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white hover:bg-coral/85 transition-colors"
            >
              Ask the concierge →
            </button>
            <Link
              to="/team"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-charcoal/15 bg-white px-4 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral transition-colors"
            >
              Set up your team
            </Link>
          </div>
        </>
      ) : (
        <p className="text-sm" style={{ color: "rgba(29,52,97,0.55)" }}>
          Try the All filter to see everything coming up.
        </p>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function memberName(c: CelebrationRow): string {
  const tm = c.team_members;
  if (!tm) return "Team member";
  if (Array.isArray(tm)) return tm[0]?.name || "Team member";
  return tm.name;
}

function humanType(type: string): string {
  if (type === "birthday") return "birthday";
  if (type === "work_anniversary") return "anniversary";
  if (type === "welcome") return "first day";
  if (type === "farewell") return "farewell";
  if (type === "milestone") return "milestone";
  return type;
}

function typeColor(type: string): string {
  if (type === "birthday") return "#C4564A";
  if (type === "work_anniversary") return "#C4943B";
  if (type === "welcome") return "#5B8C76";
  return "#6B7C9C";
}

function humanDaysOut(d: number): string {
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  return `${d} days out`;
}

function humanFullDate(iso: string): string {
  const d = parseISO(iso);
  return format(d, "EEEE MMM d");
}

function groupByDay(items: CelebrationRow[], today: Date) {
  const groups: { dayKey: string; dayLabel: string; relLabel: string; items: CelebrationRow[] }[] = [];
  const byDay = new Map<string, CelebrationRow[]>();
  for (const item of items) {
    const day = item.date;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(item);
  }
  // Sort days ascending
  const sortedDays = Array.from(byDay.keys()).sort();
  for (const dayKey of sortedDays) {
    const dayDate = parseISO(dayKey);
    const dayLabel = format(dayDate, "EEE · MMM dd");
    const days = differenceInCalendarDays(dayDate, today);
    let relLabel: string;
    if (isSameDay(dayDate, today)) relLabel = "today";
    else if (days === 1) relLabel = "tomorrow";
    else if (days < 7) relLabel = `in ${days} days`;
    else relLabel = `in ${days} days`;
    groups.push({ dayKey, dayLabel, relLabel, items: byDay.get(dayKey)! });
  }
  return groups;
}

// Demo seed: 7 celebrations across the next 8 weeks so the unauth
// /dashboard-demo route paints the prototype faithfully without
// touching any real data.
function buildDemoCelebrations(today: Date): CelebrationRow[] {
  const day = (n: number) => format(addDays(today, n), "yyyy-MM-dd");
  return [
    // Sarah is 1 day out, she's "Ready" (awaiting your nod). With the
    // 1-day approval rule, Ready maps to "tomorrow." Most others are on
    // AutoPilot since they're further out and the engine has already
    // drafted but won't pull the trigger until T-1.
    { id: "demo-1", date: day(1), type: "birthday", status: "upcoming", team_members: { name: "Sarah Chen" } },
    { id: "demo-2", date: day(7), type: "welcome", status: "upcoming", team_members: { name: "Priya Sharma" } },
    { id: "demo-3", date: day(12), type: "work_anniversary", status: "upcoming", team_members: { name: "Marcus Lee" } },
    { id: "demo-4", date: day(23), type: "birthday", status: "upcoming", team_members: { name: "Alex Reyes" } },
    { id: "demo-5", date: day(23), type: "work_anniversary", status: "upcoming", team_members: { name: "Jamie Okafor" } },
    { id: "demo-6", date: day(36), type: "birthday", status: "upcoming", team_members: { name: "Tara Nakamura" } },
    { id: "demo-7", date: day(36), type: "welcome", status: "upcoming", team_members: { name: "Diego Aranda" } },
  ];
}

// Builds the recommendation for a celebration on the dashboard.
// Engine logic lives in _shared/recommendation.ts so client + edge
// functions share one implementation. This function adapts the
// dashboard's CelebrationRow shape into the engine's input shape and
// adds the list/detail-pane extras (status pill, time label).
//
// MS78-94: dietaries, tier, and team_size are now all wired through
// to the real DB shape:
//   - dietaries: team_members.dietary_requirements (MS78)
//   - tier: team_members.tier_override → companies.default_tier
//     → "standard" fallback (MS79 + MS80 hotfix + MS90 + MS91)
//   - team_size: companies.team_size, mapped via TEAM_SIZE_HEADCOUNT
//     lookup (MS94 included the Onboarding labels that MS80 missed)
// Demo path still uses the engine's safe fallbacks so /dashboard-demo
// works without any company row.
//
// recommendationFor signature accepts company defaults so the engine
// sees real tier + team_size when they're set on the company row (and
// per-recipient tier_override on team_members). Optional with safe
// fallbacks so demo + test callers don't need updating.
function recommendationFor(
  c: CelebrationRow,
  today: Date,
  opts?: {
    companyDefaultTier?: string | null;
    companyTeamSize?: string | null;
    conciergeAutopilotOn?: boolean;
  },
): ApprovalRecommendation & {
  statusKey: "ready" | "drafting" | "autopilot" | "done" | "locked_in" | "skipped";
  statusLabel: string;
  timeLabel: string;
} {
  const date = parseISO(c.date);
  const daysOut = differenceInCalendarDays(date, today);
  const name = memberName(c);
  const firstName = name.split(" ")[0] || name;

  let statusKey: "ready" | "drafting" | "autopilot" | "done" | "locked_in" | "skipped";
  let statusLabel: string;

  if (c.status === "skipped") {
    statusKey = "skipped";
    statusLabel = "Skipped";
  } else if (c.status === "approved") {
    statusKey = "locked_in";
    statusLabel = "Needs checkout";
  } else if (c.status === "ordered") {
    statusKey = "done";
    statusLabel = "Ordered";
  } else {
    const autopilotOn = opts?.conciergeAutopilotOn === true;
    if (daysOut <= 1) {
      statusKey = "ready";
      statusLabel = "Awaiting your nod";
    } else if (autopilotOn) {
      statusKey = "autopilot";
      statusLabel = "AutoPilot";
    } else if (daysOut <= 7) {
      statusKey = "drafting";
      statusLabel = "Drafting";
    } else {
      statusKey = "drafting";
      statusLabel = "Scheduled";
    }
  }

  const occasion = c.type === "work_anniversary" ? "anniversary" : c.type === "welcome" ? "first day" : "birthday";

  // MS78 + MS79: pull the recipient's dietary_requirements + per-
  // member tier_override through from the joined team_members row.
  // Tier resolution order:
  //   1. team_members.tier_override (per-recipient explicit pick)
  //   2. companies.default_tier (company-wide default from Settings)
  //   3. "standard" (engine-safe fallback for demo + first-time use)
  // Same idea for teamSize via companies.team_size bucket.
  const tm = c.team_members;
  const tmFlat = Array.isArray(tm) ? tm[0] : tm;
  const dietaries = tmFlat?.dietary_requirements ?? [];
  const profileHints = teamMemberProfileHints(tmFlat ?? undefined);

  type Tier = "standard" | "premium" | "luxury";
  const isTier = (v: unknown): v is Tier =>
    v === "standard" || v === "premium" || v === "luxury";

  const tier: Tier = isTier(tmFlat?.tier_override)
    ? (tmFlat!.tier_override as Tier)
    : isTier(opts?.companyDefaultTier)
      ? (opts!.companyDefaultTier as Tier)
      : "standard";

  // MS80 + MS94: teamSize bucket → representative headcount. Numbers
  // picked so they round-trip back to the same bucket through the
  // engine's bucketForTeamSize ranges (team ≤12, floor ≤22, office
  // ≤35, big_days >35).
  //
  // MS94 hotfix: added the actual Onboarding bucket labels ("1-10" /
  // "11-25" / "26-50" / "51-100" / "100+") which were missing — the
  // wizard writes those values to companies.team_size but the lookup
  // only had enum strings (very_small) + V-C names (team/floor/...).
  // Net effect was that every real company silently fell back to the
  // default 18 (medium/floor), no matter what they picked at signup.
  //
  // Mappings reflect TEAM_SIZE_MAP in catalogue.ts:
  //   1-10 / very_small / small → team
  //   11-25 / medium → floor
  //   26-50 / large / office → office (mid → cap inside bucket)
  //   51-100 / 100+ / very_large / big_days → big_days
  const TEAM_SIZE_HEADCOUNT: Record<string, number> = {
    // Onboarding labels (TEAM_SIZE_OPTIONS in companyPrefs.ts)
    "1-10": 8,
    "11-25": 18,
    "26-50": 30,
    "51-100": 75,
    "100+": 150,
    // Legacy 5×3 enum strings
    very_small: 6,
    small: 10,
    medium: 18,
    large: 30,
    very_large: 75,
    // V-C era bucket names (newer wizard / API-direct callers)
    team: 8,
    floor: 18,
    office: 30,
    big_days: 75,
  };
  const teamSize =
    (opts?.companyTeamSize && TEAM_SIZE_HEADCOUNT[opts.companyTeamSize]) || 18;

  const rec = generateRecommendation({
    tier,
    teamSize,
    dietaries,
    occasion,
    recipientName: name,
    date: c.date,
    deliveryWindow: "morning_early",
    ...profileHints,
  });

  return {
    recipientName: name,
    recipientFirstName: firstName,
    occasion,
    date: c.date,
    daysOut,
    bakery: rec.bakery,
    imageUrl: rec.imageUrl,
    imageAlt: rec.imageAlt,
    productSummary: rec.productSummary,
    productLine: rec.productLine,
    cardDetail: rec.cardDetail,
    deliveryDetail: rec.deliveryDetail,
    rationaleDetail: rec.rationaleDetail,
    totalCents: rec.totalCents,
    tier: rec.tier,
    editWindowCloses: "9 am day-of",
    // MS70: pass the resolved category through so ApprovalModal's
    // first-tile emoji renders correctly (cake / hamper / florist /
    // spa / etc.) instead of falling back to the generic 🎁 gift.
    vendorCategory: rec.vendorCategory,
    // MS96: pass the engine's picked SKU id through. v1 is always
    // null today (curated copy, no SKU); ready for v2 migration so
    // handleSend can write a real line_items entry.
    skuId: rec.skuId,
    statusKey,
    statusLabel,
    // MS81: derive a tight timeLabel from the engine's deliveryWindow
    // instead of hardcoding "10:00". Default morning_early → "9 am";
    // morning_late → "10 am"; afternoon → "2 pm". When more delivery
    // windows are wired through (per-recipient preference, per-
    // celebration override), this lookup expands. Today everything
    // uses morning_early so the list pane still looks uniform — but
    // at least it's an honest uniform.
    timeLabel: DELIVERY_WINDOW_TIME_LABEL.morning_early,
  };
}

// MS81: tight time chip per delivery window. Mirrors the engine's
// formatDeliveryWindow but shorter — formatDeliveryWindow returns
// "9 – 10 am, on the day" (good for the modal body); the list-pane
// prefix needs <= 6 chars to stay tidy.
const DELIVERY_WINDOW_TIME_LABEL: Record<string, string> = {
  morning_early: "9 am",
  morning_late: "10 am",
  afternoon: "2 pm",
};

// MS45: inline sidebar icon components (CalendarDotIcon, PeopleIcon,
// HistoryIcon, HeartIcon, ClockIcon, SparkIcon, CardIcon, BellIcon)
// moved to ConciergeLayout.
